import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import {
  LayoutDashboard, Building2, Receipt, Package, Coins, Smartphone,
  Terminal, Bell, HelpCircle, ShieldCheck, LogOut, Loader2, UserCog,
  LifeBuoy,
} from 'lucide-react';
import { instances, releases, sshConfigs, billing, androidBuilds, maintenance, backups, magSend } from '../../services/superuserApi';
import { useSuperUserAuth } from '../../contexts/SuperUserAuthContext';
import ModulesPanel from './ModulesPanel';
import BackupsPanel from './BackupsPanel';
import DomainPanel from './DomainPanel';
import PlatformPanel from './PlatformPanel';
import ProvisioningPanel from './ProvisioningPanel';
import VersionPanel from './VersionPanel';
import SubscriptionTimeline from '../../components/superuser/SubscriptionTimeline';
import AutoBlockPanel from '../../components/superuser/AutoBlockPanel';
import MagSendPanel from '../../components/superuser/MagSendPanel';
import SupportPanel from './SupportPanel';
import './superuser-theme.css';

// Perfis lógicos de IA. Que modelo serve cada um é decisão do MMIA — o GyMag não o sabe.
const AI_PROFILES = [
  { value: 'default', label: 'Padrão' },
  { value: 'fast', label: 'Rápido' },
  { value: 'analysis', label: 'Análise' },
  { value: 'reasoning', label: 'Raciocínio' },
  { value: 'code', label: 'Código' },
];

// ============================================
// MAIN DASHBOARD COMPONENT
// ============================================
const SuperUserDashboard = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const { superUser, logout } = useSuperUserAuth();

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', Icon: LayoutDashboard },
    { id: 'instances', label: 'Instâncias', Icon: Building2 },
    { id: 'platform', label: 'Plataforma', Icon: UserCog },
    { id: 'billing', label: 'Faturação', Icon: Receipt },
    { id: 'plans', label: 'Planos', Icon: Package },
    { id: 'credits', label: 'Créditos', Icon: Coins },
    { id: 'androidbuilds', label: 'Android Builds', Icon: Smartphone },
    { id: 'ssh', label: 'SSH', Icon: Terminal },
    { id: 'support', label: 'Suporte', Icon: LifeBuoy },
    { id: 'notifications', label: 'Notificações', Icon: Bell },
    { id: 'help', label: 'Ajuda', Icon: HelpCircle },
  ];

  return (
    <div className="su-root" style={styles.root}>
      {/* Sidebar */}
      <aside style={styles.sidebar}>
        <div style={styles.sidebarHeader}>
          <div style={{
            width: '36px', height: '36px', borderRadius: 'var(--radius-md)',
            background: 'var(--accent-gradient)', display: 'flex', alignItems: 'center',
            justifyContent: 'center', flexShrink: 0, boxShadow: 'var(--shadow-md)',
          }}>
            <ShieldCheck size={19} color="#fff" />
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: '700', fontSize: '0.875rem', color: 'var(--text-primary)' }}>GyMag</div>
            <div style={{ fontSize: '0.75rem', color: 'var(--accent-primary-hover)', fontWeight: '500' }}>Super Admin</div>
          </div>
        </div>

        <nav style={styles.sidebarNav}>
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`su-nav-item${activeTab === tab.id ? ' is-active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <tab.Icon size={16} style={{ flexShrink: 0 }} />
              <span>{tab.label}</span>
            </button>
          ))}
        </nav>

        <div style={styles.sidebarFooter}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.5rem 0.75rem' }}>
            <div style={{
              width: '32px', height: '32px', borderRadius: 'var(--radius-sm)',
              background: 'var(--accent-primary)', display: 'flex', alignItems: 'center',
              justifyContent: 'center', color: '#fff', fontSize: '0.75rem', fontWeight: '700', flexShrink: 0,
            }}>
              {superUser?.name?.[0]?.toUpperCase() || 'S'}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: '0.75rem', fontWeight: '500', color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{superUser?.name}</div>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-faint)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{superUser?.email}</div>
            </div>
            <button
              onClick={logout}
              title="Sair"
              style={{ background: 'none', border: 'none', padding: '0.25rem', cursor: 'pointer', color: 'var(--text-faint)', display: 'flex', flexShrink: 0 }}
            >
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main style={styles.mainContent}>
        {activeTab === 'dashboard' && <DashboardTab />}
        {activeTab === 'instances' && <InstancesTab />}
        {activeTab === 'platform' && <PlatformPanel />}
        {activeTab === 'billing' && <BillingTab />}
        {activeTab === 'plans' && <PlansTab />}
        {activeTab === 'credits' && <CreditsTab />}
        {activeTab === 'androidbuilds' && <AndroidBuildsTab />}
        {activeTab === 'ssh' && <SshTab />}
        {activeTab === 'support' && <SupportPanel />}
        {activeTab === 'notifications' && <NotificationsTab />}
        {activeTab === 'help' && <HelpTab />}
      </main>
    </div>
  );
};

// ============================================
// HELP TAB - DOCUMENTATION
// ============================================
function HelpTab() {
  const publicDomain = 'https://gymbooking.magweb.ddnsfree.com';
  const googleFitRedirectPath = '/api/healthintegration/googlefit/callback';

  const Copy = ({ value }) => (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
      <span style={styles.monoBox}>{value}</span>
      <button
        style={{ ...styles.tinyBtn, background: 'var(--accent-primary)', color: '#fff' }}
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(value);
            toast.success('Copiado');
          } catch {
            toast.info('Copie manualmente');
          }
        }}
      >
        Copiar
      </button>
    </span>
  );

  return (
    <div>
      <h1 style={styles.pageTitle}>Ajuda / Documentação</h1>

      <div style={{ ...styles.tableCard, marginBottom: '1rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontWeight: '800', color: 'var(--text-primary)', fontSize: '1.05rem' }}>Guia rápido</div>
            <div style={{ color: 'var(--text-muted)', marginTop: '0.25rem' }}>
              Aqui tens o passo-a-passo para configurar HTTPS, Google Fit (OAuth) e sincronização automática.
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <button
              style={styles.secondaryBtn}
              onClick={() => window.open(`${publicDomain}/smartwatch`, '_blank')}
            >
              Abrir Smartwatch (site)
            </button>
            <button
              style={styles.secondaryBtn}
              onClick={() => window.open(`${publicDomain}/admin/settings?tab=integrations`, '_blank')}
            >
              Abrir Integrações (Admin)
            </button>
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: '1rem' }}>
        <div style={styles.tableCard}>
          <h3 style={{ marginTop: 0, color: 'var(--text-primary)' }}>1) Domínio e HTTPS</h3>
          <ol style={{ color: 'var(--text-secondary)', paddingLeft: '1.1rem' }}>
            <li>Usar sempre o domínio público (recomendado): <Copy value={publicDomain} /></li>
            <li>Nginx Proxy Manager deve encaminhar esse domínio para o backend (porta interna 3333).</li>
            <li>Garantir que o proxy envia cabeçalhos: <span style={styles.monoInline}>X-Forwarded-Proto</span> e <span style={styles.monoInline}>X-Forwarded-Host</span>.</li>
          </ol>
          <div style={styles.tipBox}>
            <strong>Atalho:</strong> no detalhe de uma instância há <strong>Criar domínio e certificado</strong>. Com a
            integração do Nginx Proxy Manager configurada no servidor, é um clique (Proxy Host, HTTPS forçado, HTTP/2
            e certificado pedido). Sem essa configuração, os passos abaixo continuam a ser os certos.
          </div>
          <div style={styles.tipBox}>
            <strong>Nota:</strong> o login/LocalStorage é por domínio. Ao mudar de domínio, os utilizadores vão ter de fazer login novamente.
          </div>
        </div>

        <div style={styles.tableCard}>
          <h3 style={{ marginTop: 0, color: 'var(--text-primary)' }}>1.1) Nginx Proxy Manager (passo-a-passo)</h3>
          <details style={styles.details} open>
            <summary style={styles.summary}>Proxy Host</summary>
            <ol style={{ color: 'var(--text-secondary)', paddingLeft: '1.1rem' }}>
              <li><strong>Domain Names</strong>: <Copy value="gymbooking.magweb.ddnsfree.com" /></li>
              <li><strong>Scheme</strong>: http</li>
              <li><strong>Forward Hostname / IP</strong>: IP interno do servidor (ex: 192.168.x.x)</li>
              <li><strong>Forward Port</strong>: <span style={styles.monoInline}>3333</span></li>
              <li>Ativar <strong>Websockets Support</strong></li>
              <li>Ativar <strong>Block Common Exploits</strong></li>
              <li>Guardar</li>
            </ol>
          </details>

          <details style={styles.details}>
            <summary style={styles.summary}>SSL (Let’s Encrypt)</summary>
            <ol style={{ color: 'var(--text-secondary)', paddingLeft: '1.1rem' }}>
              <li>Aba <strong>SSL</strong> → Request a new SSL Certificate</li>
              <li>Selecionar <strong>Force SSL</strong></li>
              <li>Selecionar <strong>HTTP/2 Support</strong></li>
              <li>Selecionar <strong>HSTS Enabled</strong> (recomendado depois de tudo estar a funcionar)</li>
              <li>Guardar</li>
            </ol>
          </details>

          <details style={styles.details}>
            <summary style={styles.summary}>Cabeçalhos (Advanced)</summary>
            <div style={{ color: 'var(--text-secondary)' }}>
              Normalmente o NPM já envia isto, mas se usares configuração avançada:
              <div style={{ marginTop: '0.5rem' }}>
                <div style={styles.monoBox}>proxy_set_header X-Forwarded-Proto $scheme;
proxy_set_header X-Forwarded-Host $host;
proxy_set_header Host $host;</div>
              </div>
            </div>
          </details>
        </div>

        <div style={styles.tableCard}>
          <h3 style={{ marginTop: 0, color: 'var(--text-primary)' }}>2) Google Fit (OAuth) — Console</h3>
          <div style={{ color: 'var(--text-secondary)' }}>
            <details style={styles.details} open>
              <summary style={styles.summary}>Criar OAuth Client (Web)</summary>
              <ol style={{ paddingLeft: '1.1rem' }}>
                <li>Google Cloud → APIs e serviços → Biblioteca → ativar <strong>Fitness API</strong></li>
                <li>Configurar <strong>Tela de permissão OAuth</strong> (tipo Externo)</li>
                <li>Criar credenciais → <strong>ID do cliente OAuth</strong> → tipo <strong>Web</strong></li>
                <li>Authorized JavaScript origins:</li>
              </ol>
              <div style={{ marginTop: '0.5rem' }}>
                <Copy value={publicDomain} />
              </div>
              <div style={{ marginTop: '0.75rem' }}>
                <div style={{ fontWeight: 700, marginBottom: '0.25rem' }}>Authorized redirect URI</div>
                <Copy value={`${publicDomain}${googleFitRedirectPath}`} />
              </div>
            </details>

            <details style={styles.details}>
              <summary style={styles.summary}>Erros comuns</summary>
              <ul style={{ paddingLeft: '1.1rem' }}>
                <li><strong>redirect_uri_mismatch</strong>: o URI tem de ser igual (https, domínio, path, sem “:3333”).</li>
                <li><strong>invalid_client</strong>: ClientId/ClientSecret errados em <strong>/admin/settings → Integrações</strong> (ou secret foi rodado no Google Cloud e não foi atualizado).</li>
                <li><strong>access blocked</strong>: consent screen incompleto / app não verificada / scopes sensíveis (em modo teste, usa utilizadores de teste).</li>
                <li><strong>App em modo teste</strong>: se estiver “Testing”, tens de adicionar utilizadores de teste no consent screen.</li>
              </ul>
            </details>

            <details style={styles.details}>
              <summary style={styles.summary}>Como editar/rodar credenciais</summary>
              <ol style={{ paddingLeft: '1.1rem' }}>
                <li>Google Cloud → Credentials → selecionar o OAuth Client</li>
                <li>Alterar <strong>Authorized origins</strong> e <strong>Redirect URIs</strong> se mudares de domínio</li>
                <li>Se o <strong>Client Secret</strong> for exposto: clicar <strong>Reset secret</strong></li>
                <li>Atualizar o novo secret em <strong>/admin/settings → Integrações → Google Fit</strong></li>
              </ol>
            </details>
          </div>
        </div>

        <div style={styles.tableCard}>
          <h3 style={{ marginTop: 0, color: 'var(--text-primary)' }}>3) Google Fit — Admin Config (na app)</h3>
          <ol style={{ color: 'var(--text-secondary)', paddingLeft: '1.1rem' }}>
            <li>Abrir <strong>/admin/settings</strong> → separador <strong>Integrações</strong> → categoria <strong>Google Fit</strong></li>
            <li>Ativar: <span style={styles.monoInline}>GoogleFit:Enabled = true</span></li>
            <li>Preencher <span style={styles.monoInline}>GoogleFit:ClientId</span> e <span style={styles.monoInline}>GoogleFit:ClientSecret</span></li>
            <li>Confirmar Redirect URI:</li>
          </ol>
          <Copy value={`${publicDomain}${googleFitRedirectPath}`} />
          <div style={{ ...styles.tipBox, marginTop: '0.75rem' }}>
            <strong>Segurança:</strong> nunca partilhar o Client Secret. Se for exposto, gerar um novo no Google Cloud.
          </div>
        </div>

        <div style={styles.tableCard}>
          <h3 style={{ marginTop: 0, color: 'var(--text-primary)' }}>4) Cliente — ligar e sincronizar</h3>
          <ol style={{ color: 'var(--text-secondary)', paddingLeft: '1.1rem' }}>
            <li>Cliente entra em <strong>/smartwatch</strong></li>
            <li>Clicar <strong>Conectar Google Fit</strong> e aceitar permissões (1x)</li>
            <li>Após ligar, sincroniza automaticamente e fica com sync diário no servidor</li>
          </ol>
          <div style={styles.tipBox}>
            <strong>Importante:</strong> permissões do Google Fit não podem ser concedidas automaticamente — o utilizador tem de aceitar.
          </div>
        </div>

        <div style={styles.tableCard}>
          <h3 style={{ marginTop: 0, color: 'var(--text-primary)' }}>6) Sync diário (servidor)</h3>
          <ul style={{ color: 'var(--text-secondary)', paddingLeft: '1.1rem' }}>
            <li>Depois do cliente ligar 1x, o servidor sincroniza automaticamente 1x/dia.</li>
            <li>Se o cliente abrir /smartwatch, também pode forçar “Sincronizar Agora”.</li>
          </ul>
          <div style={styles.tipBox}>
            <strong>Dica:</strong> se quiseres mudar a hora do sync, pede e ajusta-se facilmente.
          </div>
        </div>

        <div style={styles.tableCard}>
          <h3 style={{ marginTop: 0, color: 'var(--text-primary)' }}>7) Checklist Produção</h3>
          <details style={styles.details} open>
            <summary style={styles.summary}>Segurança</summary>
            <ul style={{ paddingLeft: '1.1rem', color: 'var(--text-secondary)' }}>
              <li>Nunca partilhar <strong>Client Secret</strong> / passwords / tokens em chat</li>
              <li>Rodar segredos se forem expostos (Google/Stripe/etc.)</li>
              <li>Confirmar que o site está sempre em <strong>HTTPS</strong> (Force SSL)</li>
              <li>Ativar HSTS depois de tudo estável</li>
            </ul>
          </details>

          <details style={styles.details}>
            <summary style={styles.summary}>Backups</summary>
            <ul style={{ paddingLeft: '1.1rem', color: 'var(--text-secondary)' }}>
              <li>Backup diário das bases de dados (master + tenants)</li>
              <li>Backup do volume do site (wwwroot + ficheiros relevantes)</li>
              <li>Testar restore (pelo menos 1x/mês)</li>
            </ul>
          </details>

          <details style={styles.details}>
            <summary style={styles.summary}>Observabilidade</summary>
            <ul style={{ paddingLeft: '1.1rem', color: 'var(--text-secondary)' }}>
              <li>Ver logs do container quando houver falhas (ex: OAuth errors, sync errors)</li>
              <li>Monitorizar CPU/RAM/Disco do host</li>
              <li>Monitorizar espaço livre do volume</li>
            </ul>
          </details>

          <details style={styles.details}>
            <summary style={styles.summary}>Troubleshooting rápido</summary>
            <ul style={{ paddingLeft: '1.1rem', color: 'var(--text-secondary)' }}>
              <li>Se OAuth falhar: confirmar Redirect URI (igual no Google Cloud e no <strong>/admin/settings → Integrações</strong>)</li>
              <li>Se 401 inesperado: confirmar login no domínio correto (novo domínio ≠ domínio antigo)</li>
              <li>Se sync não importa nada: pode não haver sessões novas no Google Fit ou scopes insuficientes</li>
            </ul>
          </details>
        </div>

        <div style={styles.tableCard}>
          <h3 style={{ marginTop: 0, color: 'var(--text-primary)' }}>5) /superuser e /superadmin</h3>
          <ul style={{ color: 'var(--text-secondary)', paddingLeft: '1.1rem' }}>
            <li><strong>/superadmin</strong> redireciona para <strong>/superuser</strong></li>
            <li><strong>/superadmin/login</strong> redireciona para <strong>/superuser/login</strong></li>
            <li>O painel SuperUser é central (esta página)</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

// ============================================
// DASHBOARD TAB - KPIs
// ============================================
function DashboardTab() {
  const [data, setData] = useState(null);
  const [instanceList, setInstanceList] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      billing.getDashboard().catch(() => ({ data: {} })),
      instances.getAll().catch(() => ({ data: [] }))
    ]).then(([dashRes, instRes]) => {
      setData(dashRes.data);
      setInstanceList(Array.isArray(instRes.data) ? instRes.data : []);
    }).finally(() => setLoading(false));
  }, []);

  if (loading) return <Spinner />;

  const d = data || {};

  return (
    <div>
      <h1 style={styles.pageTitle}>Dashboard Geral</h1>

      <div style={styles.kpiGrid}>
        <KpiCard label="Instâncias" value={d.totalInstances || 0} color="var(--accent-primary)" />
        <KpiCard label="Activas" value={d.activeInstances || 0} color="var(--success)" />
        <KpiCard label="Bloqueadas" value={d.blockedInstances || 0} color="var(--error)" />
        <KpiCard label="Subscrições" value={d.activeSubscriptions || 0} color="var(--accent-secondary)" />
        <KpiCard label="Receita Total" value={`€${(d.totalRevenue || 0).toFixed(2)}`} color="var(--warning)" />
        <KpiCard label="Receita Mensal" value={`€${(d.monthRevenue || 0).toFixed(2)}`} color="var(--success)" />
        <KpiCard label="Faturas Pendentes" value={d.pendingInvoices || 0} color="var(--warning)" />
        <KpiCard label="Em Atraso" value={d.overdueInvoices || 0} color="var(--error)" />
      </div>

      <h3 style={{ marginTop: '2rem', marginBottom: '1rem', color: 'var(--text-primary)' }}>Estado das Instâncias</h3>
      <div style={styles.tableCard}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Nome</th>
              <th style={styles.th}>Container</th>
              <th style={styles.th}>Porta</th>
              <th style={styles.th}>BD</th>
              <th style={styles.th}>Estado</th>
              <th style={styles.th}>Activa</th>
            </tr>
          </thead>
          <tbody>
            {instanceList.map(inst => (
              <tr key={inst.id}>
                <td style={styles.td}><strong>{inst.name}</strong></td>
                <td style={styles.td}>{inst.containerName}</td>
                <td style={styles.td}>{inst.containerPort}</td>
                <td style={styles.td}>{inst.mysqlDatabase}</td>
                <td style={styles.td}><StatusBadge status={inst.status} /></td>
                <td style={styles.td}>
                  <span style={{ color: inst.isActive ? 'var(--success)' : 'var(--error)', fontWeight: '600' }}>
                    {inst.isActive ? 'Sim' : 'Não'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <SchemaMaintenanceCard />
    </div>
  );
}

// ============================================
// MANUTENÇÃO DO SCHEMA DESTA INSTALAÇÃO
// ============================================
// O /health/ready só responde 200 depois de a base desta instalação estar carimbada com a
// versão do schema, e quem carimba é o provisionamento — que existia só para as instâncias
// que o portal publica. Este painel é a forma de o mandar correr aqui, e de ver o que fez.
function SchemaMaintenanceCard() {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);

  const load = React.useCallback(async () => {
    try {
      const res = await maintenance.getSchema();
      setState(res.data);
    } catch (e) {
      setState({ detail: apiError(e, 'não foi possível ler o estado do schema') });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const provision = async () => {
    if (!window.confirm('Criar o que falta na base desta instalação e carimbar a versão?\n\nO provisionamento nunca apaga nada: só acrescenta tabelas e colunas em falta.')) return;
    setBusy(true);
    setReport(null);
    try {
      const res = await maintenance.provisionSchema();
      setReport({ ok: true, ...res.data });
      toast.success(res.data?.message || 'Schema provisionado');
    } catch (e) {
      const data = e?.response?.data;
      setReport({ ok: false, ...(data || {}) });
      toast.error(data?.message || apiError(e, 'o provisionamento falhou'));
    } finally {
      setBusy(false);
      load();
    }
  };

  const provisioned = state?.provisioned;

  return (
    <div style={{ ...styles.tableCard, marginTop: '2rem', padding: '1.25rem' }}>
      <h3 style={{ margin: '0 0 .5rem', color: 'var(--text-primary)' }}>Schema desta instalação</h3>
      <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '1rem' }}>
        {state
          ? <>
              <strong style={{ color: provisioned ? 'var(--success)' : 'var(--warning)' }}>
                {provisioned ? 'provisionado' : 'por provisionar'}
              </strong>
              {' — '}{state.detail || ''}
              {/* o detail já diz a versão e a contagem quando está provisionado; a contagem
                  só se acrescenta quando não está, que é quando ele não a traz */}
              {!provisioned && state.tables != null && <> · {state.tables} tabelas</>}
            </>
          : 'a ler…'}
      </div>

      <button className="btn btn-primary" onClick={provision} disabled={busy || provisioned}>
        {busy ? 'A provisionar…' : provisioned ? 'Nada a fazer' : 'Provisionar schema'}
      </button>

      {report && (
        <div style={{ marginTop: '1rem', fontSize: '12px', color: 'var(--text-secondary)' }}>
          <div style={{ fontWeight: 600, color: report.ok ? 'var(--success)' : 'var(--error)', marginBottom: '.35rem' }}>
            {report.message}
          </div>
          {Array.isArray(report.steps) && (
            <ul style={{ margin: 0, paddingLeft: '1.1rem' }}>
              {report.steps.map((s, i) => <li key={i}>{s}</li>)}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================
// INSTANCES TAB
// ============================================
// O backend explica porque recusou (porta ocupada, container inexistente, nome duplicado).
// Engolir isso e mostrar "Erro ao criar instancia" obriga a ir aos logs para saber o que
// se passou. Truncado para nao despejar output enorme do Docker no toast.
function apiError(e, fallback) {
  const msg = e?.response?.data?.message || e?.response?.data?.title;
  if (!msg) return fallback;
  return msg.length > 300 ? msg.slice(0, 300) + '...' : msg;
}

function InstancesTab() {
  const [instanceList, setInstanceList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedInstance, setSelectedInstance] = useState(null);
  const [detail, setDetail] = useState(null);
  const [sshConfigList, setSshConfigList] = useState([]);
  const [currentRelease, setCurrentRelease] = useState(null);
  const [deploying, setDeploying] = useState({});
  const [deployReport, setDeployReport] = useState(null);
  const [updatingAiModel, setUpdatingAiModel] = useState({});
  const [updatingPublicUrl, setUpdatingPublicUrl] = useState({});
  // Acção em lote «Ligar todos ao MagSend» (a decorrer).
  const [linkingAll, setLinkingAll] = useState(false);
  // Última cópia por instância, para a coluna de estado dizer a verdade sem abrir o detalhe.
  const [backupSummary, setBackupSummary] = useState({});
  const [contactData, setContactData] = useState(null);
  const [editingContact, setEditingContact] = useState(false);
  const [contactForm, setContactForm] = useState({ adminName: '', adminEmail: '', adminPhone: '', companyName: '', nif: '', address: '', postalCode: '', city: '' });
  const [newInstance, setNewInstance] = useState({
    name: '', description: '', containerName: '', containerVolumePath: '', publicUrl: '',
    // Sem valor por omissao: cada instancia tem o seu utilizador dedicado, e "root" e
    // recusado pelo servidor. Ter "root" aqui garantia um 400 em cada criacao.
    containerPort: 3333, mysqlHost: 'mariadb', mysqlPort: 3306, mysqlUser: '',
    mysqlPassword: '',
    mysqlDatabase: '',
    sshConfigurationId: null,
    aiModelName: 'default',
  });

  // Estado a serio vem do Docker, nao da coluna Status.
  const STATUS_BY_NAME = {
    Pending: 0, Deploying: 1, Running: 2, Stopped: 3, Error: 4,
    Missing: 5, Installing: 6, Updating: 7, Unhealthy: 8,
  };

  const refreshRuntimeStatus = useCallback(async (list) => {
    await Promise.all((list || []).map(async (inst) => {
      try {
        const res = await instances.getStatus(inst.id);
        const name = res.data?.instanceStatus;
        if (!name) return;
        setInstanceList(prev => prev.map(i =>
          i.id === inst.id ? { ...i, status: STATUS_BY_NAME[name] ?? i.status, runtimeChecked: true } : i));
      } catch {
        // Sem SSH ou host inacessivel: marcar como nao verificado, em vez de mostrar o
        // valor antigo como se fosse actual.
        setInstanceList(prev => prev.map(i =>
          i.id === inst.id ? { ...i, runtimeUnknown: true } : i));
      }
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // silent: volta a ler a lista SEM trocar a tabela por um spinner — quem está a preencher
  // vários campos seguidos (MagSend por ginásio) não pode perder o que escreveu a seguir.
  const loadData = useCallback(async ({ silent = false } = {}) => {
    try {
      if (!silent) setLoading(true);
      const [instRes, sshRes, relRes, backupRes] = await Promise.all([
        instances.getAll().catch(() => ({ data: [] })),
        sshConfigs.getAll().catch(() => ({ data: [] })),
        releases.getCurrent().catch(() => ({ data: null })),
        backups.summary().catch(() => ({ data: null })),
      ]);
      const list = Array.isArray(instRes.data) ? instRes.data : [];
      setInstanceList(list);
      setSshConfigList(Array.isArray(sshRes.data) ? sshRes.data : []);
      // A release corrente e a unica coisa instalavel. Sem ela nao se oferece
      // instalacao nenhuma, em vez de prometer um botao que ia falhar no servidor.
      setCurrentRelease(relRes.data || null);
      // Sem esta chamada a coluna dizia «sem cópia» sobre um ginásio copiado há uma hora.
      const byInstance = {};
      (backupRes?.data?.instances || []).forEach(b => { byInstance[b.instanceId] = b; });
      setBackupSummary(byInstance);
      // O estado na tabela e apenas a ultima intencao registada: fica "Deploying" para
      // sempre se um deploy morreu a meio, e nao sabe que o container foi removido por
      // fora. Perguntar ao Docker por instancia e reconciliar, sem bloquear a lista.
      refreshRuntimeStatus(list);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const currentVersion = currentRelease?.version || null;

  const isOutdated = (instVersion) => {
    if (!currentVersion || !instVersion) return false;
    const parse = v => (v || '0.0.0').split('.').map(Number);
    const [a, b] = [parse(currentVersion), parse(instVersion)];
    for (let i = 0; i < 3; i++) {
      if ((a[i] || 0) > (b[i] || 0)) return true;
      if ((a[i] || 0) < (b[i] || 0)) return false;
    }
    return false;
  };

  // Instalar e actualizar sao coisas diferentes e nao sao intercambiaveis: oferecer
  // "Atualizar" numa instancia que nunca foi instalada e prometer um restart de um
  // container que nao existe -- era exactamente o que o painel antigo fazia.
  //  - Sem container (Missing/Pending) ........... Instalar
  //  - A correr numa versao anterior ............. Atualizar
  //  - A correr na versao corrente ............... nada a fazer
  //  - Error/Unhealthy ........................... repetir a operacao que faz sentido
  const deployPlan = (inst) => {
    if (!currentRelease) return { kind: 'none', reason: 'Nenhuma release corrente definida' };
    if (inst.runtimeUnknown) return { kind: 'none', reason: 'Estado do host desconhecido' };

    const neverInstalled = inst.status === 5 || inst.status === 0 || !inst.appVersion;
    if (neverInstalled) return { kind: 'install', label: 'Instalar' };
    if (inst.status === 6 || inst.status === 7) return { kind: 'none', reason: 'Deploy em curso' };
    if (inst.status === 4 || inst.status === 8)
      return { kind: 'update', label: 'Repetir atualização', danger: true };
    if (isOutdated(inst.appVersion)) return { kind: 'update', label: 'Atualizar' };
    return { kind: 'none' };
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await instances.create(newInstance);
      toast.success('Instância criada!');
      setShowCreate(false);
      loadData();
    } catch (e) { toast.error(apiError(e, 'Erro ao criar instância')); }
  };

  const handleAction = async (id, action) => {
    const actionLabels = { start: 'Iniciar', stop: 'Parar', restart: 'Reiniciar', deploy: 'Deploy' };
    const label = actionLabels[action] || action;
    if (!window.confirm(`Tem a certeza que deseja "${label}" esta instância?`)) return;
    try {
      await instances.executeAction(id, action);
      toast.success(`Ação '${label}' executada com sucesso!`);
      loadData();
    } catch (e) { toast.error(apiError(e, `Erro ao ${label.toLowerCase()}`)); }
  };

  const handleDeploy = async (inst, kind) => {
    if (!currentRelease) return;
    const isInstall = kind === 'install';
    const question = isInstall
      ? `Instalar ${currentRelease.version} em "${inst.name}"?\n\n`
        + `Cria o container ${inst.containerName} na porta ${inst.containerPort} a partir de `
        + `${currentRelease.imageTag}. A base de dados ${inst.mysqlDatabase} não é apagada em nenhum caso.`
      : `Atualizar "${inst.name}" de v${inst.appVersion || '?'} para ${currentRelease.version}?\n\n`
        + 'O container é recriado a partir da imagem nova. Se o health-check falhar, a versão '
        + 'anterior é reposta automaticamente.';
    if (!window.confirm(question)) return;

    setDeploying(prev => ({ ...prev, [inst.id]: true }));
    setDeployReport(null);
    try {
      const call = isInstall ? instances.install : instances.updateRelease;
      const res = await call(inst.id, currentRelease.id);
      toast.success(res.data?.message || 'Concluído');
      setDeployReport({ instanceId: inst.id, name: inst.name, ...res.data });
      loadData();
    } catch (e) {
      // Um deploy falhado tem de dizer em que fase falhou. Um "Erro" sozinho obrigava
      // a ir ao servidor ver logs para saber se foi a porta, a imagem ou a base de dados.
      const data = e.response?.data;
      toast.error(data?.message || apiError(e, 'Erro no deploy'));
      if (data?.steps) setDeployReport({ instanceId: inst.id, name: inst.name, ...data });
      loadData();
    } finally {
      setDeploying(prev => ({ ...prev, [inst.id]: false }));
    }
  };

  // O endereço por onde o sócio entra. A app Android abre-o, e o caminho de receção do
  // MagSend constrói-se a partir dele — sem isto, nenhum dos dois sabe onde está o ginásio.
  const handleUpdatePublicUrl = async (id, publicUrl) => {
    setUpdatingPublicUrl(prev => ({ ...prev, [id]: true }));
    try {
      await instances.update(id, { publicUrl });
      toast.success('Endereço público guardado');
      loadData();
    } catch (e) {
      toast.error(apiError(e, 'Erro ao guardar o endereço público'));
    } finally {
      setUpdatingPublicUrl(prev => ({ ...prev, [id]: false }));
    }
  };

  // Os ginásios que já existem: uma acção em lote que cria o projecto e a chave de cada um.
  // Não reinicia containers — um lote que reinicia todos ao mesmo tempo corta o serviço a todos;
  // a configuração entra em vigor na próxima actualização ou com «Aplicar agora» na ficha.
  const handleLinkAllMagSend = async () => {
    if (!window.confirm('Ligar ao MagSend todos os ginásios que ainda não têm projecto e chave próprios?')) return;
    setLinkingAll(true);
    try {
      const res = await magSend.linkAll();
      const data = res.data;
      toast.success(data.message || `${data.linked} ginásio(s) ligados.`);
      const failed = (data.results || []).filter(r => !r.ok);
      if (failed.length > 0) {
        toast.info(`Ficaram por ligar: ${failed.map(f => f.name).join(', ')}`);
      }
      loadData({ silent: true });
    } catch (e) {
      toast.error(apiError(e, 'Erro ao ligar os ginásios ao MagSend'));
    } finally {
      setLinkingAll(false);
    }
  };

  const handleUpdateAiModel = async (id, aiModelName) => {
    setUpdatingAiModel(prev => ({ ...prev, [id]: true }));
    try {
      await instances.update(id, { aiModelName });
      toast.success('Perfil de IA atualizado');
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Erro ao atualizar perfil de IA');
    } finally {
      setUpdatingAiModel(prev => ({ ...prev, [id]: false }));
    }
  };

  const handleBlock = async (id, isActive) => {
    const msg = isActive ? 'Bloquear esta instância? O ginásio perderá acesso.' : 'Desbloquear esta instância?';
    if (!window.confirm(msg)) return;
    try {
      if (isActive) {
        await billing.blockInstance(id, 'Bloqueado pelo SuperAdmin');
      } else {
        await billing.unblockInstance(id);
      }
      toast.success(isActive ? 'Instância bloqueada' : 'Instância desbloqueada');
      loadData();
    } catch (e) { toast.error('Erro'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminar permanentemente esta instância? Esta ação é irreversível!')) return;
    try {
      await instances.delete(id);
      toast.success('Instância eliminada');
      loadData();
    } catch (e) { toast.error('Erro ao eliminar'); }
  };

  const viewDetail = async (id) => {
    try {
      const [detRes, contactRes] = await Promise.all([
        billing.getInstanceDetail(id),
        billing.getContact(id).catch(() => ({ data: null }))
      ]);
      setDetail(detRes.data);
      setSelectedInstance(id);
      const c = contactRes.data;
      if (c && c.companyName) {
        setContactData(c);
        setContactForm({ adminName: c.adminName || '', adminEmail: c.adminEmail || '', adminPhone: c.adminPhone || '', companyName: c.companyName || '', nif: c.nif || '', address: c.address || '', postalCode: c.postalCode || '', city: c.city || '' });
      } else {
        setContactData(null);
        setContactForm({ adminName: '', adminEmail: '', adminPhone: '', companyName: '', nif: '', address: '', postalCode: '', city: '' });
      }
      setEditingContact(false);
    } catch (e) { toast.error('Erro ao carregar detalhe'); }
  };

  const handleSaveContact = async () => {
    try {
      await billing.updateContact(selectedInstance, contactForm);
      toast.success('Dados de faturação guardados!');
      setEditingContact(false);
      const contactRes = await billing.getContact(selectedInstance);
      setContactData(contactRes.data);
    } catch (e) { toast.error('Erro ao guardar dados de faturação'); }
  };

  if (loading) return <Spinner />;

  // Detail view
  if (selectedInstance && detail) {
    const inst = detail.instance;
    const sub = detail.subscription;
    const bill = detail.billing;
    const instFull = instanceList.find(i => i.id === selectedInstance);
    return (
      <div>
        <button style={styles.backBtn} onClick={() => { setSelectedInstance(null); setDetail(null); setContactData(null); }}>← Voltar</button>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem', marginBottom: '1.5rem' }}>
          <div>
            <h1 style={{ ...styles.pageTitle, marginBottom: '0.25rem' }}>{inst.name}</h1>
            <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
              <span style={{ padding: '3px 10px', borderRadius: '4px', fontSize: '0.78rem', fontWeight: '600', background: 'var(--bg-tertiary)', color: 'var(--accent-primary)', fontFamily: 'monospace' }}>v{instFull?.appVersion || '1.0.0'}</span>
              <StatusBadge status={instFull?.status} />
              {!inst.isActive && <span style={{ padding: '2px 8px', borderRadius: '4px', fontSize: '0.72rem', background: 'var(--error-bg)', color: 'var(--error-text)', fontWeight: '600' }}>BLOQUEADA</span>}
            </div>
          </div>
          {instFull && (() => {
            const plan = deployPlan(instFull);
            if (plan.kind === 'none') return null;
            return (
              <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                <span style={{ fontSize: '0.8rem', color: 'var(--warning)' }}>
                  {plan.kind === 'install'
                    ? `Nunca instalada — release ${currentVersion} disponível`
                    : `Atualização disponível — v${currentVersion}`}
                </span>
                <button
                  style={{ ...styles.primaryBtn, background: plan.danger ? 'var(--warning)' : 'var(--success)' }}
                  onClick={() => handleDeploy(instFull, plan.kind)}
                  disabled={!!deploying[instFull.id]}
                >
                  {deploying[instFull.id] ? 'A executar...' : plan.label}
                </button>
              </div>
            );
          })()}
        </div>

        {deployReport?.instanceId === selectedInstance && (
          <DeployReport report={deployReport} onClose={() => setDeployReport(null)} />
        )}

        <div style={styles.kpiGrid}>
          <KpiCard label="Container" value={inst.containerName} color="var(--accent-primary)" />
          <KpiCard label="Porta" value={inst.containerPort} color="var(--success)" />
          <KpiCard label="Base de Dados" value={inst.mysqlDatabase} color="var(--accent-secondary)" />
          <KpiCard label="Estado" value={inst.isActive ? 'Activa' : 'Bloqueada'} color={inst.isActive ? 'var(--success)' : 'var(--error)'} />
        </div>

        {/* Onde vai a instalação, passo a passo (com polling): uma falha deixou de ficar «a instalar»
            para sempre, e uma instalação sem progresso aparece como parada. */}
        <ProvisioningPanel instanceId={inst.id} />

        {/* A versão REAL: release e imagem registadas, comparação com o que corre (OK/DRIFT/
            MISSING/STOPPED/UNKNOWN com motivo) e a reparação a partir do registo. */}
        <VersionPanel instanceId={inst.id} />

        <ModulesPanel tenantKey={inst.name} />

        {/* Domínio e certificado HTTPS: um pedido ao Nginx Proxy Manager em vez dos ~15 minutos
            de cliques. Sem a integração configurada, o painel diz isso e mantém as instruções
            manuais em Ajuda. */}
        <DomainPanel instanceId={inst.id} instanceName={inst.name} />

        {/* Cópias de segurança: última cópia, cópia manual, configuração e reposição. */}
        <BackupsPanel instanceId={inst.id} instanceName={inst.name} />

        {/* Dados de Faturação do Cliente */}
        <div style={{ ...styles.tableCard, marginTop: '1rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ margin: 0, fontSize: '1rem', color: 'var(--text-primary)' }}>Dados de Faturação</h3>
            {!editingContact ? (
              <button style={{ ...styles.smallBtn, background: 'var(--accent-soft)', color: 'var(--accent-primary)' }} onClick={() => setEditingContact(true)}>
                {contactData ? 'Editar' : 'Configurar'}
              </button>
            ) : (
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button style={{ ...styles.smallBtn, background: 'var(--success-bg)', color: 'var(--success)' }} onClick={handleSaveContact}>Guardar</button>
                <button style={styles.smallBtn} onClick={() => setEditingContact(false)}>Cancelar</button>
              </div>
            )}
          </div>
          {editingContact ? (
            <div style={{ ...styles.formGrid, marginTop: '1rem' }}>
              <FormField label="Empresa / Nome" value={contactForm.companyName} onChange={v => setContactForm({ ...contactForm, companyName: v })} />
              <FormField label="NIF / NIPC" value={contactForm.nif} onChange={v => setContactForm({ ...contactForm, nif: v })} />
              <FormField label="Responsável" value={contactForm.adminName} onChange={v => setContactForm({ ...contactForm, adminName: v })} />
              <FormField label="Email" value={contactForm.adminEmail} onChange={v => setContactForm({ ...contactForm, adminEmail: v })} />
              <FormField label="Telefone" value={contactForm.adminPhone} onChange={v => setContactForm({ ...contactForm, adminPhone: v })} />
              <FormField label="Morada" value={contactForm.address} onChange={v => setContactForm({ ...contactForm, address: v })} />
              <FormField label="Código Postal" value={contactForm.postalCode} onChange={v => setContactForm({ ...contactForm, postalCode: v })} />
              <FormField label="Cidade" value={contactForm.city} onChange={v => setContactForm({ ...contactForm, city: v })} />
            </div>
          ) : contactData && contactData.companyName ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.75rem', marginTop: '0.75rem', fontSize: '0.875rem' }}>
              <div><strong>Empresa:</strong> {contactData.companyName}</div>
              <div><strong>NIF:</strong> {contactData.nif || '—'}</div>
              <div><strong>Responsável:</strong> {contactData.adminName || '—'}</div>
              <div><strong>Email:</strong> {contactData.adminEmail || '—'}</div>
              <div><strong>Telefone:</strong> {contactData.adminPhone || '—'}</div>
              <div><strong>Morada:</strong> {contactData.address || '—'}</div>
              <div><strong>Código Postal:</strong> {contactData.postalCode || '—'}</div>
              <div><strong>Cidade:</strong> {contactData.city || '—'}</div>
            </div>
          ) : (
            <div style={{ marginTop: '0.75rem', padding: '1rem', background: 'var(--warning-bg)', borderRadius: '8px', color: 'var(--warning-text)', fontSize: '0.85rem' }}>
              Sem dados de faturação configurados. Clique em "Configurar" para associar um cliente a esta instância.
            </div>
          )}
        </div>

        {sub && (
          <div style={{ ...styles.tableCard, marginTop: '1rem' }}>
            <h3 style={{ margin: 0, fontSize: '1rem', color: 'var(--text-primary)', marginBottom: '0.75rem' }}>Subscrição Activa</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem', marginTop: '0.75rem' }}>
              <div><strong>Plano:</strong> {sub.planIcon} {sub.planName}</div>
              <div><strong>Ciclo:</strong> {sub.billingCycle === 'annual' ? 'Anual' : 'Mensal'}</div>
              <div><strong>Preço:</strong> €{sub.currentPrice?.toFixed(2)}</div>
              <div><strong>Próx. Faturação:</strong> {new Date(sub.nextBillingDate).toLocaleDateString('pt-PT')}</div>
            </div>
          </div>
        )}

        {/* O histórico auditável desta instância: antes/depois, autor, origem e motivo — e, com
            subscrição activa, o formulário de correção de validade (que exige motivo). */}
        <SubscriptionTimeline instanceId={inst.id} subscriptionId={sub?.id} />

        <div style={{ ...styles.tableCard, marginTop: '1rem' }}>
          <h3 style={{ margin: 0, fontSize: '1rem', color: 'var(--text-primary)', marginBottom: '0.75rem' }}>Resumo Financeiro</h3>
          <div style={{ display: 'flex', gap: '2rem' }}>
            <div><strong>Total Faturas:</strong> {bill?.totalInvoices || 0}</div>
            <div><strong>Faturas Pendentes:</strong> <span style={{ color: 'var(--warning)' }}>{bill?.pendingInvoices || 0}</span></div>
            <div><strong>Total Pago:</strong> <span style={{ color: 'var(--success)', fontWeight: '700' }}>€{(bill?.totalPaid || 0).toFixed(2)}</span></div>
          </div>
        </div>

        {/* Créditos (Tokens IA + SMS) */}
        <div style={{ ...styles.tableCard, marginTop: '1rem' }}>
          <h3 style={{ margin: 0, fontSize: '1rem', color: 'var(--text-primary)', marginBottom: '0.75rem' }}>Créditos</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem' }}>
            <div style={{ background: 'var(--bg-tertiary)', padding: '1rem', borderRadius: '8px', borderLeft: '3px solid var(--accent-primary)' }}>
              <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Tokens IA</div>
              <div style={{ fontSize: '1.75rem', fontWeight: '700', color: 'var(--accent-primary)', margin: '4px 0' }}>{detail.credits?.tokenBalance || 0}</div>
              <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>Comprados: {detail.credits?.totalTokensPurchased || 0} · Consumidos: {detail.credits?.totalTokensConsumed || 0}</div>
            </div>
            <div style={{ background: 'var(--bg-tertiary)', padding: '1rem', borderRadius: '8px', borderLeft: '3px solid var(--success)' }}>
              <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>SMS</div>
              <div style={{ fontSize: '1.75rem', fontWeight: '700', color: 'var(--success)', margin: '4px 0' }}>{detail.credits?.smsBalance || 0}</div>
              <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>Comprados: {detail.credits?.totalSmsPurchased || 0} · Consumidos: {detail.credits?.totalSmsConsumed || 0}</div>
            </div>
          </div>
        </div>

        {detail.recentAuditLogs?.length > 0 && (
          <div style={{ ...styles.tableCard, marginTop: '1rem' }}>
            <h3 style={{ margin: 0, fontSize: '1rem', color: 'var(--text-primary)', marginBottom: '0.75rem' }}>Histórico Recente</h3>
            <div style={{ marginTop: '0.75rem' }}>
              {detail.recentAuditLogs.map((log, i) => (
                <div key={i} style={{ padding: '0.5rem 0', borderBottom: '1px solid var(--bg-tertiary)', fontSize: '0.875rem' }}>
                  <strong>{getAuditIcon(log.action)}</strong> {log.details}
                  <span style={{ float: 'right', color: 'var(--text-muted)', fontSize: '0.75rem' }}>
                    {new Date(log.createdAt).toLocaleDateString('pt-PT')}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem', marginBottom: '1.5rem' }}>
        <div>
          <h1 style={{ ...styles.pageTitle, marginBottom: '0.25rem' }}>Gestão de Instâncias</h1>
          {currentRelease
            ? <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Release corrente: <strong style={{ color: 'var(--accent-primary)' }}>v{currentRelease.version}</strong> <span style={{ fontFamily: 'monospace' }}>({currentRelease.imageTag})</span></span>
            : <span style={{ fontSize: '0.8rem', color: 'var(--warning)' }}>Nenhuma release corrente — registe e promova uma release antes de instalar</span>}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
          {/* Os ginásios que já existiam. Cria o projecto e a chave de cada um sem reiniciar
              containers: a configuração entra em vigor na próxima actualização, ou já com
              «Aplicar agora» na ficha do ginásio. */}
          <button
            style={{ ...styles.secondaryBtn, minHeight: 44 }}
            onClick={handleLinkAllMagSend}
            disabled={linkingAll}
            title="Criar o projecto e a chave MagSend de cada ginásio que ainda não os tem"
          >
            {linkingAll ? 'A ligar…' : 'Ligar todos ao MagSend'}
          </button>
          <button style={{ ...styles.primaryBtn, minHeight: 44 }} onClick={() => setShowCreate(true)}>+ Nova Instância</button>
        </div>
      </div>

      {deployReport && <DeployReport report={deployReport} onClose={() => setDeployReport(null)} />}

      <div style={styles.tableCard}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Instância</th>
              <th style={styles.th}>Infraestrutura</th>
              <th style={styles.th}>Perfil IA</th>
              <th style={styles.th}>Versão</th>
              <th style={styles.th}>Estado</th>
              <th style={{ ...styles.th, textAlign: 'right' }}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {instanceList.map(inst => {
              const outdated = isOutdated(inst.appVersion);
              const plan = deployPlan(inst);
              return (
                <tr key={inst.id} style={{ background: !inst.isActive ? 'var(--error-bg)' : 'transparent' }}>
                  <td style={styles.td}>
                    <div style={{ fontWeight: '600', color: 'var(--text-primary)', marginBottom: '2px' }}>{inst.name}</div>
                    {inst.description && <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{inst.description}</div>}
                  </td>
                  <td style={styles.td}>
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', lineHeight: '1.7' }}>
                      <div><span style={{ color: 'var(--text-muted)', minWidth: '70px', display: 'inline-block' }}>Container:</span> <strong>{inst.containerName}</strong></div>
                      <div><span style={{ color: 'var(--text-muted)', minWidth: '70px', display: 'inline-block' }}>Porta:</span> {inst.containerPort}</div>
                      {inst.mysqlDatabase && <div><span style={{ color: 'var(--text-muted)', minWidth: '70px', display: 'inline-block' }}>BD:</span> {inst.mysqlDatabase}</div>}
                      {/* O endereço por onde o sócio entra. A app Android abre-o e o caminho de
                          receção do MagSend constrói-se a partir dele; sem ele, o build da app
                          recusa-se a correr em vez de produzir uma app que aponta para o vazio. */}
                      <div style={{ display: 'flex', gap: '6px', alignItems: 'center', marginTop: '4px' }}>
                        <span style={{ color: 'var(--text-muted)', minWidth: '70px', display: 'inline-block' }}>Endereço:</span>
                        <input
                          style={{ ...styles.input, padding: '4px 8px', fontSize: '0.75rem', maxWidth: '230px' }}
                          defaultValue={inst.publicUrl || ''}
                          placeholder="https://ginasio.exemplo.pt"
                          disabled={!!updatingPublicUrl[inst.id]}
                          onBlur={(e) => {
                            const next = e.target.value.trim();
                            if (next === (inst.publicUrl || '')) return;
                            handleUpdatePublicUrl(inst.id, next);
                          }}
                          title="Guarda ao sair do campo"
                        />
                      </div>
                      {/* MagSend deste ginásio: um projecto e uma chave próprios, criados pela API
                          de operador. Deixaram de existir campos para colar o projecto, o alias e o
                          caminho da chave — ninguém os preenchia, e o resultado era todos os
                          ginásios a sair pelo mesmo número. */}
                      <div style={{ marginTop: '8px' }}>
                        <MagSendPanel instanceId={inst.id} instanceName={inst.name} />
                      </div>
                    </div>
                  </td>
                  <td style={styles.td}>
                    <select
                      style={{ ...styles.input, maxWidth: '220px', padding: '6px 10px', fontSize: '0.8rem' }}
                      value={inst.aiModelName || 'default'}
                      disabled={!!updatingAiModel[inst.id]}
                      onChange={(e) => {
                        const next = e.target.value;
                        if (next === inst.aiModelName) return;
                        handleUpdateAiModel(inst.id, next);
                      }}
                      title={updatingAiModel[inst.id] ? 'A atualizar...' : 'Selecionar perfil de IA'}
                    >
                      {AI_PROFILES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                    </select>
                  </td>
                  <td style={styles.td}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-start' }}>
                      {/* Uma instancia sem container nao tem versao a correr: mostrar "v1.0.0"
                          era inventar um facto sobre o servidor. */}
                      {plan.kind === 'install'
                        ? <span style={{ padding: '2px 10px', borderRadius: '4px', fontSize: '0.75rem', background: 'var(--bg-tertiary)', color: 'var(--text-muted)', fontWeight: '600' }}>não instalada</span>
                        : <span style={{ padding: '2px 10px', borderRadius: '4px', fontSize: '0.75rem', background: outdated ? 'var(--warning-bg)' : 'var(--success-bg)', color: outdated ? 'var(--warning-text)' : 'var(--success-text)', fontWeight: '600', fontFamily: 'monospace' }}>v{inst.appVersion || '1.0.0'}</span>}
                      {outdated && plan.kind !== 'install' && <span style={{ fontSize: '0.7rem', color: 'var(--warning)', fontWeight: '500' }}>Atualização disponível</span>}
                      {inst.lastUpdatedAt && <span style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>{new Date(inst.lastUpdatedAt).toLocaleDateString('pt-PT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>}
                    </div>
                  </td>
                  <td style={styles.td}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-start' }}>
                      <StatusBadge status={inst.status} />
                      {(() => {
                        const bkp = backupSummary[inst.id];
                        if (!bkp) return null;
                        const when = bkp.lastBackupAt
                          ? new Date(bkp.lastBackupAt).toLocaleString('pt-PT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
                          : null;
                        const ok = bkp.lastStatus === 'Success';
                        const color = !when ? 'var(--text-muted)' : ok ? 'var(--success)' : 'var(--error)';
                        return (
                          <span data-testid={`last-backup-${inst.id}`} style={{ fontSize: '0.7rem', color, fontWeight: '500' }}>
                            Cópia: {when ? `${when} · ${ok ? 'ok' : 'falhou'}` : 'nunca'}
                            {!bkp.enabled && ' (auto desligada)'}
                          </span>
                        );
                      })()}
                      {!inst.isActive && <span style={{ padding: '2px 8px', borderRadius: '4px', fontSize: '0.7rem', background: 'var(--error-bg)', color: 'var(--error-text)', fontWeight: '600', letterSpacing: '0.025em' }}>BLOQUEADA</span>}
                    </div>
                  </td>
                  <td style={{ ...styles.td, textAlign: 'right' }}>
                    <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                      <button style={instActionBtn('var(--accent-primary)')} onClick={() => viewDetail(inst.id)}>Detalhe</button>
                      {plan.kind !== 'none' && (
                        <button
                          style={instActionBtn(plan.danger ? 'var(--warning)' : 'var(--success)')}
                          onClick={() => handleDeploy(inst, plan.kind)}
                          disabled={!!deploying[inst.id]}
                        >
                          {deploying[inst.id] ? 'A executar...' : plan.label}
                        </button>
                      )}
                      <button style={instActionBtn('var(--success)')} onClick={() => handleAction(inst.id, 'start')}>Iniciar</button>
                      <button style={instActionBtn('var(--warning)')} onClick={() => handleAction(inst.id, 'restart')}>Reiniciar</button>
                      <button style={instActionBtn('var(--text-muted)')} onClick={() => handleAction(inst.id, 'stop')}>Parar</button>
                      <button style={instActionBtn(inst.isActive ? 'var(--error)' : 'var(--success)')} onClick={() => handleBlock(inst.id, inst.isActive)}>
                        {inst.isActive ? 'Bloquear' : 'Desbloquear'}
                      </button>
                      <button style={{ ...instActionBtn('var(--error)'), background: 'transparent', color: 'var(--error)', border: '1px solid var(--error-text)' }} onClick={() => handleDelete(inst.id)}>Eliminar</button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Create Modal */}
      {showCreate && (
        <Modal title="Nova Instância" onClose={() => setShowCreate(false)}>
          <form onSubmit={handleCreate}>
            <div style={styles.formGrid}>
              <FormField label="Nome *" value={newInstance.name} onChange={v => setNewInstance({...newInstance, name: v})} required />
              <FormField label="Container Name *" value={newInstance.containerName} onChange={v => setNewInstance({...newInstance, containerName: v})} required />
              <FormField label="Porta *" type="number" value={newInstance.containerPort} onChange={v => setNewInstance({...newInstance, containerPort: parseInt(v)})} required />
              <FormField label="Host MySQL *" value={newInstance.mysqlHost} onChange={v => setNewInstance({...newInstance, mysqlHost: v})} required />
              <FormField label="Base de Dados MySQL *" value={newInstance.mysqlDatabase} onChange={v => setNewInstance({...newInstance, mysqlDatabase: v})} required />
              <FormField label="Utilizador MySQL *" value={newInstance.mysqlUser} onChange={v => setNewInstance({...newInstance, mysqlUser: v})} placeholder="ex.: gym_adriano_app (root é recusado)" required />
              <FormField label="Password MySQL *" type="password" value={newInstance.mysqlPassword} onChange={v => setNewInstance({...newInstance, mysqlPassword: v})} required />
              <div style={{ marginBottom: '1rem' }}>
                <label style={styles.label}>Perfil de IA *</label>
                <select style={styles.input} value={newInstance.aiModelName} onChange={e => setNewInstance({...newInstance, aiModelName: e.target.value})} required>
                  {AI_PROFILES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
              </div>
            </div>
            <FormField label="Endereço público" value={newInstance.publicUrl}
              onChange={v => setNewInstance({...newInstance, publicUrl: v})}
              placeholder="https://ginasio.exemplo.pt" />
            <FormField label="Descrição" value={newInstance.description} onChange={v => setNewInstance({...newInstance, description: v})} textarea />
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
              <button type="button" style={styles.secondaryBtn} onClick={() => setShowCreate(false)}>Cancelar</button>
              <button type="submit" style={styles.primaryBtn}>Criar Instância</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

// ============================================
// BILLING TAB - Subscriptions, Invoices, Payments
// ============================================
function BillingTab() {
  const [subTab, setSubTab] = useState('subscriptions');
  const [subscriptions, setSubscriptions] = useState([]);
  const [invoiceList, setInvoiceList] = useState([]);
  const [paymentList, setPaymentList] = useState([]);
  const [planList, setPlanList] = useState([]);
  const [instanceList, setInstanceList] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAssignPlan, setShowAssignPlan] = useState(false);
  const [showCreateInvoice, setShowCreateInvoice] = useState(false);
  const [showRecordPayment, setShowRecordPayment] = useState(false);

  // Form states
  const [assignForm, setAssignForm] = useState({ gymInstanceId: '', tenantPlanId: '', billingCycle: 'monthly', trialDays: 0 });
  const [invoiceForm, setInvoiceForm] = useState({ gymInstanceId: '', description: '', subtotal: '', taxRate: 23, notes: '' });
  const [paymentForm, setPaymentForm] = useState({ tenantInvoiceId: '', amount: '', paymentMethod: 'transfer', transactionReference: '', notes: '' });

  const loadData = useCallback(async () => {
    try {
      const [subsRes, invRes, payRes, planRes, instRes, auditRes] = await Promise.all([
        billing.getSubscriptions().catch(() => ({ data: [] })),
        billing.getInvoices().catch(() => ({ data: [] })),
        billing.getPayments().catch(() => ({ data: [] })),
        billing.getPlans().catch(() => ({ data: [] })),
        instances.getAll().catch(() => ({ data: [] })),
        billing.getAuditLog().catch(() => ({ data: [] })),
      ]);
      setSubscriptions(Array.isArray(subsRes.data) ? subsRes.data : []);
      setInvoiceList(Array.isArray(invRes.data) ? invRes.data : []);
      setPaymentList(Array.isArray(payRes.data) ? payRes.data : []);
      setPlanList(Array.isArray(planRes.data) ? planRes.data : []);
      setInstanceList(Array.isArray(instRes.data) ? instRes.data : []);
      setAuditLog(Array.isArray(auditRes.data) ? auditRes.data : []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleAssignPlan = async (e) => {
    e.preventDefault();
    try {
      await billing.createSubscription({
        gymInstanceId: parseInt(assignForm.gymInstanceId),
        tenantPlanId: parseInt(assignForm.tenantPlanId),
        billingCycle: assignForm.billingCycle,
        trialDays: assignForm.trialDays
      });
      toast.success('Plano atribuído!');
      setShowAssignPlan(false);
      loadData();
    } catch (e) { toast.error(e.response?.data?.message || 'Erro'); }
  };

  const handleCreateInvoice = async (e) => {
    e.preventDefault();
    try {
      const res = await billing.createInvoice({
        gymInstanceId: parseInt(invoiceForm.gymInstanceId),
        description: invoiceForm.description,
        subtotal: parseFloat(invoiceForm.subtotal),
        taxRate: parseInt(invoiceForm.taxRate),
        notes: invoiceForm.notes || null
      });
      toast.success(`Fatura ${res.data.invoiceNumber} criada!`);
      setShowCreateInvoice(false);
      loadData();
    } catch (e) { toast.error('Erro ao criar fatura'); }
  };

  const handleGenerateInvoice = async (gymInstanceId) => {
    try {
      const res = await billing.generateInvoice(gymInstanceId);
      toast.success(`Fatura ${res.data.invoiceNumber} gerada automaticamente!`);
      loadData();
    } catch (e) { toast.error(e.response?.data?.message || 'Erro ao gerar fatura'); }
  };

  const handleRecordPayment = async (e) => {
    e.preventDefault();
    try {
      await billing.recordPayment({
        tenantInvoiceId: parseInt(paymentForm.tenantInvoiceId),
        amount: parseFloat(paymentForm.amount),
        paymentMethod: paymentForm.paymentMethod,
        transactionReference: paymentForm.transactionReference || null,
        notes: paymentForm.notes || null
      });
      toast.success('Pagamento registado!');
      setShowRecordPayment(false);
      loadData();
    } catch (e) { toast.error('Erro ao registar pagamento'); }
  };

  const handleInvoiceStatus = async (id, status) => {
    try {
      await billing.updateInvoiceStatus(id, status);
      toast.success('Estado atualizado');
      loadData();
    } catch (e) { toast.error('Erro'); }
  };

  if (loading) return <Spinner />;

  const subTabs = [
    { id: 'subscriptions', label: '📋 Subscrições' },
    { id: 'invoices', label: `📄 Faturas (${invoiceList.length})` },
    { id: 'payments', label: '💳 Pagamentos' },
    { id: 'audit', label: '📜 Audit Log' },
  ];

  return (
    <div>
      <h1 style={styles.pageTitle}>💰 Faturação & Pagamentos</h1>

      <div style={styles.subTabs}>
        {subTabs.map(t => (
          <button key={t.id} style={{ ...styles.subTab, ...(subTab === t.id ? styles.subTabActive : {}) }}
            onClick={() => setSubTab(t.id)}>{t.label}</button>
        ))}
      </div>

      {/* SUBSCRIPTIONS */}
      {subTab === 'subscriptions' && (
        <div>
          {/* O interruptor, a carência e a lista de quem seria bloqueado hoje. Ler a lista é
              possível antes de ligar: é para isso que serve o modo simular. */}
          <AutoBlockPanel />

          <div style={{ display: 'flex', justifyContent: 'flex-end', margin: '1rem 0' }}>
            <button style={styles.primaryBtn} onClick={() => setShowAssignPlan(true)}>+ Atribuir Plano</button>
          </div>

          <div style={styles.tableCard}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Instância</th>
                  <th style={styles.th}>Plano</th>
                  <th style={styles.th}>Ciclo</th>
                  <th style={styles.th}>Preço</th>
                  <th style={styles.th}>Próx. Faturação</th>
                  <th style={styles.th}>Estado</th>
                  <th style={styles.th}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {subscriptions.length === 0 ? (
                  <tr><td colSpan={7} style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>Sem subscrições</td></tr>
                ) : subscriptions.map(s => (
                  <tr key={s.id}>
                    <td style={styles.td}><strong>{s.gymName}</strong></td>
                    <td style={styles.td}>{s.planIcon} {s.planName}</td>
                    <td style={styles.td}>{s.billingCycle === 'annual' ? 'Anual' : 'Mensal'}</td>
                    <td style={styles.td}>€{s.currentPrice?.toFixed(2)}</td>
                    <td style={styles.td}>{new Date(s.nextBillingDate).toLocaleDateString('pt-PT')}</td>
                    <td style={styles.td}><SubStatusBadge status={s.status} /></td>
                    <td style={styles.td}>
                      <div style={{ display: 'flex', gap: '0.25rem' }}>
                        {s.status === 'active' && <button style={{ ...styles.tinyBtn, background: 'var(--warning-bg)', color: 'var(--warning)' }} onClick={() => billing.suspendSubscription(s.id, 'Suspensa no painel de faturação').then(() => { toast.success('Suspensa'); loadData(); })}>⏸ Suspender</button>}
                        {s.status === 'suspended' && <button style={{ ...styles.tinyBtn, background: 'var(--success-bg)', color: 'var(--success)' }} onClick={() => billing.reactivateSubscription(s.id, 'Reactivada no painel de faturação').then(() => { toast.success('Reactivada'); loadData(); })}>▶ Reactivar</button>}
                        <button
                          style={{ ...styles.tinyBtn, background: 'var(--accent-soft)', color: 'var(--accent-primary)' }}
                          title="Estende a validade um ciclo a partir do fim do período actual"
                          onClick={() => billing.renewSubscription(s.id, 'Renovação registada no painel de faturação')
                            .then((r) => { toast.success(r.data?.message || 'Renovada'); if (r.data?.reactivated) toast.info('Instância reactivada'); loadData(); })
                            .catch((e) => toast.error(e.response?.data?.message || 'Não foi possível renovar'))}
                        >🔁 Renovar</button>
                        <button style={{ ...styles.tinyBtn, background: 'var(--accent-soft)', color: 'var(--accent-primary)' }} onClick={() => handleGenerateInvoice(s.gymInstanceId)}>📄 Faturar</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {showAssignPlan && (
            <Modal title="📋 Atribuir Plano a Instância" onClose={() => setShowAssignPlan(false)}>
              <form onSubmit={handleAssignPlan}>
                <div style={{ marginBottom: '1rem' }}>
                  <label style={styles.label}>Instância</label>
                  <select style={styles.input} value={assignForm.gymInstanceId} onChange={e => setAssignForm({...assignForm, gymInstanceId: e.target.value})} required>
                    <option value="">Selecione...</option>
                    {instanceList.map(i => <option key={i.id} value={i.id}>{i.name}</option>)}
                  </select>
                </div>
                <div style={{ marginBottom: '1rem' }}>
                  <label style={styles.label}>Plano</label>
                  <select style={styles.input} value={assignForm.tenantPlanId} onChange={e => setAssignForm({...assignForm, tenantPlanId: e.target.value})} required>
                    <option value="">Selecione...</option>
                    {planList.filter(p => p.isActive).map(p => <option key={p.id} value={p.id}>{p.icon} {p.name} - €{p.monthlyPrice}/mês</option>)}
                  </select>
                </div>
                <div style={styles.formGrid}>
                  <div>
                    <label style={styles.label}>Ciclo</label>
                    <select style={styles.input} value={assignForm.billingCycle} onChange={e => setAssignForm({...assignForm, billingCycle: e.target.value})}>
                      <option value="monthly">Mensal</option>
                      <option value="annual">Anual</option>
                    </select>
                  </div>
                  <div>
                    <label style={styles.label}>Dias Trial</label>
                    <input type="number" min="0" style={styles.input} value={assignForm.trialDays} onChange={e => setAssignForm({...assignForm, trialDays: parseInt(e.target.value) || 0})} />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
                  <button type="button" style={styles.secondaryBtn} onClick={() => setShowAssignPlan(false)}>Cancelar</button>
                  <button type="submit" style={styles.primaryBtn}>Atribuir Plano</button>
                </div>
              </form>
            </Modal>
          )}
        </div>
      )}

      {/* INVOICES */}
      {subTab === 'invoices' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
            <button style={styles.primaryBtn} onClick={() => setShowCreateInvoice(true)}>+ Nova Fatura</button>
          </div>

          <div style={styles.tableCard}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Nº Fatura</th>
                  <th style={styles.th}>Instância</th>
                  <th style={styles.th}>Descrição</th>
                  <th style={styles.th}>Subtotal</th>
                  <th style={styles.th}>IVA</th>
                  <th style={styles.th}>Total</th>
                  <th style={styles.th}>Estado</th>
                  <th style={styles.th}>Vencimento</th>
                  <th style={styles.th}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {invoiceList.length === 0 ? (
                  <tr><td colSpan={9} style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>Sem faturas</td></tr>
                ) : invoiceList.map(inv => (
                  <tr key={inv.id}>
                    <td style={{ ...styles.td, fontWeight: '600' }}>{inv.invoiceNumber}</td>
                    <td style={styles.td}>{inv.gymName}</td>
                    <td style={styles.td}>{inv.description}</td>
                    <td style={styles.td}>€{inv.subtotal?.toFixed(2)}</td>
                    <td style={styles.td}>€{inv.taxAmount?.toFixed(2)}</td>
                    <td style={{ ...styles.td, fontWeight: '700' }}>€{inv.total?.toFixed(2)}</td>
                    <td style={styles.td}><InvoiceStatusBadge status={inv.status} /></td>
                    <td style={styles.td}>{new Date(inv.dueDate).toLocaleDateString('pt-PT')}</td>
                    <td style={styles.td}>
                      <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap' }}>
                        {inv.status === 'pending' && (
                          <>
                            <button style={{ ...styles.tinyBtn, background: 'var(--success-bg)', color: 'var(--success)' }} onClick={() => { setPaymentForm({ tenantInvoiceId: inv.id.toString(), amount: inv.total?.toString(), paymentMethod: 'transfer', transactionReference: '', notes: '' }); setShowRecordPayment(true); }}>💰 Pagar</button>
                            <button style={{ ...styles.tinyBtn, background: 'var(--error-bg)', color: 'var(--error)' }} onClick={() => handleInvoiceStatus(inv.id, 'cancelled')}>❌ Anular</button>
                          </>
                        )}
                        {inv.status === 'overdue' && <button style={{ ...styles.tinyBtn, background: 'var(--success-bg)', color: 'var(--success)' }} onClick={() => { setPaymentForm({ tenantInvoiceId: inv.id.toString(), amount: inv.total?.toString(), paymentMethod: 'transfer', transactionReference: '', notes: '' }); setShowRecordPayment(true); }}>💰 Pagar</button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {showCreateInvoice && (
            <Modal title="📄 Nova Fatura" onClose={() => setShowCreateInvoice(false)}>
              <form onSubmit={handleCreateInvoice}>
                <div style={{ marginBottom: '1rem' }}>
                  <label style={styles.label}>Instância</label>
                  <select style={styles.input} value={invoiceForm.gymInstanceId} onChange={e => setInvoiceForm({...invoiceForm, gymInstanceId: e.target.value})} required>
                    <option value="">Selecione...</option>
                    {instanceList.map(i => <option key={i.id} value={i.id}>{i.name}</option>)}
                  </select>
                </div>
                <FormField label="Descrição" value={invoiceForm.description} onChange={v => setInvoiceForm({...invoiceForm, description: v})} />
                <div style={styles.formGrid}>
                  <FormField label="Subtotal (€) *" type="number" value={invoiceForm.subtotal} onChange={v => setInvoiceForm({...invoiceForm, subtotal: v})} required />
                  <FormField label="IVA (%)" type="number" value={invoiceForm.taxRate} onChange={v => setInvoiceForm({...invoiceForm, taxRate: v})} />
                </div>
                <FormField label="Notas" value={invoiceForm.notes} onChange={v => setInvoiceForm({...invoiceForm, notes: v})} textarea />
                <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
                  <button type="button" style={styles.secondaryBtn} onClick={() => setShowCreateInvoice(false)}>Cancelar</button>
                  <button type="submit" style={styles.primaryBtn}>Criar Fatura</button>
                </div>
              </form>
            </Modal>
          )}

          {showRecordPayment && (
            <Modal title="💳 Registar Pagamento" onClose={() => setShowRecordPayment(false)}>
              <form onSubmit={handleRecordPayment}>
                <div style={{ marginBottom: '1rem' }}>
                  <label style={styles.label}>Fatura</label>
                  <select style={styles.input} value={paymentForm.tenantInvoiceId} onChange={e => setPaymentForm({...paymentForm, tenantInvoiceId: e.target.value})} required>
                    <option value="">Selecione...</option>
                    {invoiceList.filter(i => i.status !== 'paid' && i.status !== 'cancelled').map(i => <option key={i.id} value={i.id}>{i.invoiceNumber} - €{i.total?.toFixed(2)} ({i.gymName})</option>)}
                  </select>
                </div>
                <div style={styles.formGrid}>
                  <FormField label="Montante (€) *" type="number" value={paymentForm.amount} onChange={v => setPaymentForm({...paymentForm, amount: v})} required />
                  <div>
                    <label style={styles.label}>Método de Pagamento</label>
                    <select style={styles.input} value={paymentForm.paymentMethod} onChange={e => setPaymentForm({...paymentForm, paymentMethod: e.target.value})}>
                      <option value="transfer">Transferência Bancária</option>
                      <option value="mbway">MBWay</option>
                      <option value="card">Cartão</option>
                      <option value="cash">Numerário</option>
                    </select>
                  </div>
                </div>
                <FormField label="Referência da Transação" value={paymentForm.transactionReference} onChange={v => setPaymentForm({...paymentForm, transactionReference: v})} />
                <FormField label="Notas" value={paymentForm.notes} onChange={v => setPaymentForm({...paymentForm, notes: v})} textarea />
                <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
                  <button type="button" style={styles.secondaryBtn} onClick={() => setShowRecordPayment(false)}>Cancelar</button>
                  <button type="submit" style={styles.primaryBtn}>Registar Pagamento</button>
                </div>
              </form>
            </Modal>
          )}
        </div>
      )}

      {/* PAYMENTS */}
      {subTab === 'payments' && (
        <div style={styles.tableCard}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Data</th>
                <th style={styles.th}>Instância</th>
                <th style={styles.th}>Fatura</th>
                <th style={styles.th}>Montante</th>
                <th style={styles.th}>Método</th>
                <th style={styles.th}>Referência</th>
              </tr>
            </thead>
            <tbody>
              {paymentList.length === 0 ? (
                <tr><td colSpan={6} style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>Sem pagamentos</td></tr>
              ) : paymentList.map(p => (
                <tr key={p.id}>
                  <td style={styles.td}>{new Date(p.paidAt).toLocaleDateString('pt-PT')}</td>
                  <td style={styles.td}>{p.gymName}</td>
                  <td style={styles.td}>{p.invoiceNumber}</td>
                  <td style={{ ...styles.td, fontWeight: '700', color: 'var(--success)' }}>€{p.amount?.toFixed(2)}</td>
                  <td style={styles.td}>{getPaymentMethodLabel(p.paymentMethod)}</td>
                  <td style={styles.td}>{p.transactionReference || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* AUDIT LOG */}
      {subTab === 'audit' && (
        <div style={styles.tableCard}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Data</th>
                <th style={styles.th}>Instância</th>
                <th style={styles.th}>Ação</th>
                <th style={styles.th}>Detalhes</th>
              </tr>
            </thead>
            <tbody>
              {auditLog.length === 0 ? (
                <tr><td colSpan={4} style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>Sem registos</td></tr>
              ) : auditLog.map(l => (
                <tr key={l.id}>
                  <td style={styles.td}>{new Date(l.createdAt).toLocaleDateString('pt-PT')}</td>
                  <td style={styles.td}>{l.gymName}</td>
                  <td style={styles.td}>{getAuditIcon(l.action)} {l.action}</td>
                  <td style={styles.td}>{l.details}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ============================================
// PLANS TAB
// ============================================
function PlansTab() {
  const [planList, setPlanList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const emptyForm = {
    name: '', description: '', monthlyPrice: '', annualPrice: '',
    maxClients: 50, maxTrainers: 5, monthlyTokenAllowance: 500,
    hasAI: false, hasBilling: false, hasCRM: false, hasReports: true,
    icon: '📦', isPopular: false, sortOrder: 1, isActive: true
  };
  const [form, setForm] = useState(emptyForm);

  const loadPlans = useCallback(async () => {
    try {
      const res = await billing.getPlans();
      setPlanList(Array.isArray(res.data) ? res.data : []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadPlans(); }, [loadPlans]);

  const openCreate = () => {
    setEditId(null);
    setForm(emptyForm);
    setShowForm(true);
  };

  const openEdit = (plan) => {
    setEditId(plan.id);
    setForm({
      name: plan.name || '',
      description: plan.description || '',
      monthlyPrice: plan.monthlyPrice?.toString() || '',
      annualPrice: plan.annualPrice?.toString() || '',
      maxClients: plan.maxClients || 50,
      maxTrainers: plan.maxTrainers || 5,
      monthlyTokenAllowance: plan.monthlyTokenAllowance || 500,
      hasAI: !!plan.hasAI,
      hasBilling: !!plan.hasBilling,
      hasCRM: !!plan.hasCRM,
      hasReports: !!plan.hasReports,
      icon: plan.icon || '📦',
      isPopular: !!plan.isPopular,
      sortOrder: plan.sortOrder || 1,
      isActive: plan.isActive !== false
    });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        monthlyPrice: parseFloat(form.monthlyPrice),
        annualPrice: form.annualPrice ? parseFloat(form.annualPrice) : null,
      };
      if (editId) {
        await billing.updatePlan(editId, payload);
        toast.success('Plano atualizado!');
      } else {
        await billing.createPlan(payload);
        toast.success('Plano criado!');
      }
      setShowForm(false);
      setEditId(null);
      loadPlans();
    } catch (e) { toast.error('Erro ao guardar plano'); }
  };

  const handleDelete = async (plan) => {
    if (!window.confirm(`Desactivar o plano "${plan.name}"?`)) return;
    try {
      await billing.deletePlan(plan.id);
      toast.success('Plano desactivado');
      loadPlans();
    } catch (e) { toast.error('Erro'); }
  };

  if (loading) return <Spinner />;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1 style={styles.pageTitle}>📦 Planos de Subscrição</h1>
        <button style={styles.primaryBtn} onClick={openCreate}>+ Novo Plano</button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem', marginTop: '1.5rem' }}>
        {planList.map(p => (
          <div key={p.id} style={{
            ...styles.tableCard,
            textAlign: 'center',
            position: 'relative',
            border: p.isPopular ? '2px solid var(--accent-primary)' : '1px solid var(--border-color)',
            opacity: p.isActive ? 1 : 0.5
          }}>
            {p.isPopular && <div style={{ position: 'absolute', top: '-12px', left: '50%', transform: 'translateX(-50%)', background: 'var(--accent-primary)', color: 'white', padding: '2px 16px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: '700' }}>⭐ MAIS POPULAR</div>}
            
            {/* Botões Edit/Delete no canto superior direito */}
            <div style={{ position: 'absolute', top: '10px', right: '10px', display: 'flex', gap: '0.25rem' }}>
              <button style={{ ...styles.tinyBtn, background: 'var(--accent-soft)', color: 'var(--accent-primary)' }} onClick={() => openEdit(p)} title="Editar">✏️</button>
              <button style={{ ...styles.tinyBtn, background: 'var(--error-bg)', color: 'var(--error)' }} onClick={() => handleDelete(p)} title="Desactivar">🗑️</button>
            </div>

            <div style={{ fontSize: '3rem', marginBottom: '0.5rem' }}>{p.icon}</div>
            <h2 style={{ margin: '0 0 0.25rem' }}>{p.name}</h2>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginBottom: '1rem' }}>{p.description}</p>
            <div style={{ fontSize: '2.5rem', fontWeight: '800', color: 'var(--accent-primary)' }}>€{p.monthlyPrice?.toFixed(2)}</div>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '1rem' }}>/mês</div>
            {p.annualPrice && <div style={{ fontSize: '0.85rem', color: 'var(--success)', fontWeight: '600', marginBottom: '1rem' }}>ou €{p.annualPrice?.toFixed(2)}/ano</div>}
            
            <div style={{ textAlign: 'left', borderTop: '1px solid var(--bg-tertiary)', paddingTop: '1rem', fontSize: '0.85rem' }}>
              <div style={{ padding: '0.25rem 0' }}>👥 Até {p.maxClients} clientes</div>
              <div style={{ padding: '0.25rem 0' }}>💪 Até {p.maxTrainers} trainers</div>
              <div style={{ padding: '0.25rem 0' }}>🪙 {p.monthlyTokenAllowance} tokens/mês</div>
              <div style={{ padding: '0.25rem 0' }}>{p.hasAI ? '✅' : '❌'} IA / Trainer Virtual</div>
              <div style={{ padding: '0.25rem 0' }}>{p.hasBilling ? '✅' : '❌'} Módulo de Faturação</div>
              <div style={{ padding: '0.25rem 0' }}>{p.hasCRM ? '✅' : '❌'} CRM & Marketing</div>
              <div style={{ padding: '0.25rem 0' }}>{p.hasReports ? '✅' : '❌'} Relatórios</div>
            </div>

            {!p.isActive && <div style={{ marginTop: '0.75rem', padding: '4px 12px', background: 'var(--error-bg)', color: 'var(--error)', borderRadius: '8px', fontSize: '0.8rem' }}>Desactivado</div>}
          </div>
        ))}
      </div>

      {showForm && (
        <Modal title={editId ? '✏️ Editar Plano' : '📦 Novo Plano'} onClose={() => { setShowForm(false); setEditId(null); }}>
          <form onSubmit={handleSubmit}>
            <div style={styles.formGrid}>
              <FormField label="Nome *" value={form.name} onChange={v => setForm({...form, name: v})} required />
              <FormField label="Ícone" value={form.icon} onChange={v => setForm({...form, icon: v})} />
            </div>
            <FormField label="Descrição" value={form.description} onChange={v => setForm({...form, description: v})} textarea />
            <div style={styles.formGrid}>
              <FormField label="Preço Mensal (€) *" type="number" value={form.monthlyPrice} onChange={v => setForm({...form, monthlyPrice: v})} required />
              <FormField label="Preço Anual (€)" type="number" value={form.annualPrice} onChange={v => setForm({...form, annualPrice: v})} />
              <FormField label="Máx. Clientes" type="number" value={form.maxClients} onChange={v => setForm({...form, maxClients: parseInt(v)})} />
              <FormField label="Máx. Trainers" type="number" value={form.maxTrainers} onChange={v => setForm({...form, maxTrainers: parseInt(v)})} />
              <FormField label="Tokens/Mês" type="number" value={form.monthlyTokenAllowance} onChange={v => setForm({...form, monthlyTokenAllowance: parseInt(v)})} />
              <FormField label="Ordem" type="number" value={form.sortOrder} onChange={v => setForm({...form, sortOrder: parseInt(v)})} />
            </div>
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', margin: '1rem 0' }}>
              <label><input type="checkbox" checked={form.hasAI} onChange={e => setForm({...form, hasAI: e.target.checked})} /> IA</label>
              <label><input type="checkbox" checked={form.hasBilling} onChange={e => setForm({...form, hasBilling: e.target.checked})} /> Faturação</label>
              <label><input type="checkbox" checked={form.hasCRM} onChange={e => setForm({...form, hasCRM: e.target.checked})} /> CRM</label>
              <label><input type="checkbox" checked={form.hasReports} onChange={e => setForm({...form, hasReports: e.target.checked})} /> Relatórios</label>
              <label><input type="checkbox" checked={form.isPopular} onChange={e => setForm({...form, isPopular: e.target.checked})} /> ⭐ Popular</label>
              {editId && <label><input type="checkbox" checked={form.isActive} onChange={e => setForm({...form, isActive: e.target.checked})} /> Activo</label>}
            </div>
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button type="button" style={styles.secondaryBtn} onClick={() => { setShowForm(false); setEditId(null); }}>Cancelar</button>
              <button type="submit" style={styles.primaryBtn}>{editId ? 'Guardar Alterações' : 'Criar Plano'}</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

// ============================================
// CREDITS TAB - Token & SMS Packs Management
// ============================================
function CreditsTab() {
  const [subTab, setSubTab] = useState('balances');
  const [balances, setBalances] = useState([]);
  const [tokenPacks, setTokenPacks] = useState([]);
  const [smsPacks, setSmsPacks] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [instanceList, setInstanceList] = useState([]);
  const [loading, setLoading] = useState(true);

  // Form states
  const [showTokenPackForm, setShowTokenPackForm] = useState(false);
  const [showSmsPackForm, setShowSmsPackForm] = useState(false);
  const [showPurchaseForm, setShowPurchaseForm] = useState(false);
  const [editTokenPackId, setEditTokenPackId] = useState(null);
  const [editSmsPackId, setEditSmsPackId] = useState(null);

  const emptyTokenPack = { name: '', description: '', tokenAmount: '', price: '', icon: '🤖', sortOrder: 0, isActive: true };
  const emptySmsPack = { name: '', description: '', smsAmount: '', price: '', icon: '📱', sortOrder: 0, isActive: true };
  const emptyPurchase = { gymInstanceId: '', creditType: 'tokens', packId: '', notes: '' };

  const [tokenPackForm, setTokenPackForm] = useState(emptyTokenPack);
  const [smsPackForm, setSmsPackForm] = useState(emptySmsPack);
  const [purchaseForm, setPurchaseForm] = useState(emptyPurchase);

  const loadData = useCallback(async () => {
    try {
      const [balRes, tpRes, spRes, purRes, instRes] = await Promise.all([
        billing.getAllCredits().catch(() => ({ data: [] })),
        billing.getTokenPacks().catch(() => ({ data: [] })),
        billing.getSmsPacks().catch(() => ({ data: [] })),
        billing.getCreditPurchases().catch(() => ({ data: [] })),
        instances.getAll().catch(() => ({ data: [] })),
      ]);
      setBalances(Array.isArray(balRes.data) ? balRes.data : []);
      setTokenPacks(Array.isArray(tpRes.data) ? tpRes.data : []);
      setSmsPacks(Array.isArray(spRes.data) ? spRes.data : []);
      setPurchases(Array.isArray(purRes.data) ? purRes.data : []);
      setInstanceList(Array.isArray(instRes.data) ? instRes.data : []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // Token Pack CRUD
  const openTokenPackCreate = () => { setEditTokenPackId(null); setTokenPackForm(emptyTokenPack); setShowTokenPackForm(true); };
  const openTokenPackEdit = (p) => {
    setEditTokenPackId(p.id);
    setTokenPackForm({ name: p.name, description: p.description || '', tokenAmount: p.tokenAmount?.toString(), price: p.price?.toString(), icon: p.icon || '🤖', sortOrder: p.sortOrder || 0, isActive: p.isActive !== false });
    setShowTokenPackForm(true);
  };
  const handleTokenPackSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...tokenPackForm, tokenAmount: parseInt(tokenPackForm.tokenAmount), price: parseFloat(tokenPackForm.price) };
      if (editTokenPackId) {
        await billing.updateTokenPack(editTokenPackId, payload);
        toast.success('Pack de tokens atualizado!');
      } else {
        await billing.createTokenPack(payload);
        toast.success('Pack de tokens criado!');
      }
      setShowTokenPackForm(false); setEditTokenPackId(null); loadData();
    } catch (e) { toast.error('Erro ao guardar pack'); }
  };
  const handleTokenPackDelete = async (p) => {
    if (!window.confirm(`Desactivar "${p.name}"?`)) return;
    try { await billing.deleteTokenPack(p.id); toast.success('Pack desactivado'); loadData(); } catch (e) { toast.error('Erro'); }
  };

  // SMS Pack CRUD
  const openSmsPackCreate = () => { setEditSmsPackId(null); setSmsPackForm(emptySmsPack); setShowSmsPackForm(true); };
  const openSmsPackEdit = (p) => {
    setEditSmsPackId(p.id);
    setSmsPackForm({ name: p.name, description: p.description || '', smsAmount: p.smsAmount?.toString(), price: p.price?.toString(), icon: p.icon || '📱', sortOrder: p.sortOrder || 0, isActive: p.isActive !== false });
    setShowSmsPackForm(true);
  };
  const handleSmsPackSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...smsPackForm, smsAmount: parseInt(smsPackForm.smsAmount), price: parseFloat(smsPackForm.price) };
      if (editSmsPackId) {
        await billing.updateSmsPack(editSmsPackId, payload);
        toast.success('Pack de SMS atualizado!');
      } else {
        await billing.createSmsPack(payload);
        toast.success('Pack de SMS criado!');
      }
      setShowSmsPackForm(false); setEditSmsPackId(null); loadData();
    } catch (e) { toast.error('Erro ao guardar pack'); }
  };
  const handleSmsPackDelete = async (p) => {
    if (!window.confirm(`Desactivar "${p.name}"?`)) return;
    try { await billing.deleteSmsPack(p.id); toast.success('Pack desactivado'); loadData(); } catch (e) { toast.error('Erro'); }
  };

  // Purchase credits
  const handlePurchase = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        gymInstanceId: parseInt(purchaseForm.gymInstanceId),
        creditType: purchaseForm.creditType,
        packId: purchaseForm.packId ? parseInt(purchaseForm.packId) : null,
        notes: purchaseForm.notes || null
      };
      const res = await billing.purchaseCredits(payload);
      toast.success(res.data.message);
      setShowPurchaseForm(false);
      setPurchaseForm(emptyPurchase);
      loadData();
    } catch (e) { toast.error(e.response?.data?.message || 'Erro ao atribuir créditos'); }
  };

  if (loading) return <Spinner />;

  const subTabs = [
    { id: 'balances', label: '📊 Saldos por Instância' },
    { id: 'token-packs', label: `🤖 Packs Tokens (${tokenPacks.length})` },
    { id: 'sms-packs', label: `📱 Packs SMS (${smsPacks.length})` },
    { id: 'history', label: '📜 Histórico Compras' },
  ];

  const activePacks = purchaseForm.creditType === 'tokens' ? tokenPacks.filter(p => p.isActive) : smsPacks.filter(p => p.isActive);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1 style={styles.pageTitle}>🪙 Gestão de Créditos (Tokens IA & SMS)</h1>
        <button style={styles.primaryBtn} onClick={() => setShowPurchaseForm(true)}>+ Atribuir Créditos</button>
      </div>

      <div style={styles.subTabs}>
        {subTabs.map(t => (
          <button key={t.id} style={{ ...styles.subTab, ...(subTab === t.id ? styles.subTabActive : {}) }}
            onClick={() => setSubTab(t.id)}>{t.label}</button>
        ))}
      </div>

      {/* BALANCES */}
      {subTab === 'balances' && (
        <div style={styles.tableCard}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Instância</th>
                <th style={styles.th}>Estado</th>
                <th style={styles.th}>🤖 Tokens IA</th>
                <th style={styles.th}>Comprados</th>
                <th style={styles.th}>Consumidos</th>
                <th style={styles.th}>📱 SMS</th>
                <th style={styles.th}>Comprados</th>
                <th style={styles.th}>Consumidos</th>
              </tr>
            </thead>
            <tbody>
              {balances.length === 0 ? (
                <tr><td colSpan={8} style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>Sem dados</td></tr>
              ) : balances.map(b => (
                <tr key={b.gymInstanceId}>
                  <td style={styles.td}><strong>{b.gymName}</strong></td>
                  <td style={styles.td}>
                    <span style={{ color: b.isActive ? 'var(--success)' : 'var(--error)', fontWeight: '600' }}>
                      {b.isActive ? '✅' : '🚫'}
                    </span>
                  </td>
                  <td style={{ ...styles.td, fontWeight: '700', color: 'var(--accent-primary)', fontSize: '1.1rem' }}>{b.tokenBalance}</td>
                  <td style={styles.td}>{b.totalTokensPurchased}</td>
                  <td style={styles.td}>{b.totalTokensConsumed}</td>
                  <td style={{ ...styles.td, fontWeight: '700', color: 'var(--success)', fontSize: '1.1rem' }}>{b.smsBalance}</td>
                  <td style={styles.td}>{b.totalSmsPurchased}</td>
                  <td style={styles.td}>{b.totalSmsConsumed}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* TOKEN PACKS */}
      {subTab === 'token-packs' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
            <button style={styles.primaryBtn} onClick={openTokenPackCreate}>+ Novo Pack Tokens</button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '1rem' }}>
            {tokenPacks.map(p => (
              <div key={p.id} style={{ ...styles.tableCard, textAlign: 'center', opacity: p.isActive ? 1 : 0.5, position: 'relative' }}>
                <div style={{ position: 'absolute', top: '8px', right: '8px', display: 'flex', gap: '0.25rem' }}>
                  <button style={{ ...styles.tinyBtn, background: 'var(--accent-soft)', color: 'var(--accent-primary)' }} onClick={() => openTokenPackEdit(p)}>✏️</button>
                  <button style={{ ...styles.tinyBtn, background: 'var(--error-bg)', color: 'var(--error)' }} onClick={() => handleTokenPackDelete(p)}>🗑️</button>
                </div>
                <div style={{ fontSize: '2.5rem' }}>{p.icon || '🤖'}</div>
                <h3 style={{ margin: '0.5rem 0 0.25rem' }}>{p.name}</h3>
                {p.description && <p style={{ color: 'var(--text-muted)', fontSize: '0.8rem', margin: '0 0 0.5rem' }}>{p.description}</p>}
                <div style={{ fontSize: '2rem', fontWeight: '800', color: 'var(--accent-primary)' }}>{p.tokenAmount}</div>
                <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>tokens</div>
                <div style={{ fontSize: '1.25rem', fontWeight: '700', color: 'var(--success)', marginTop: '0.5rem' }}>€{p.price?.toFixed(2)}</div>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>€{(p.price / p.tokenAmount * 100)?.toFixed(2)} / 100 tokens</div>
                {!p.isActive && <div style={{ marginTop: '0.5rem', padding: '2px 8px', background: 'var(--error-bg)', color: 'var(--error)', borderRadius: '8px', fontSize: '0.75rem', display: 'inline-block' }}>Desactivado</div>}
              </div>
            ))}
            {tokenPacks.length === 0 && <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '3rem' }}>Nenhum pack de tokens criado. Clique em "+ Novo Pack Tokens".</div>}
          </div>
        </div>
      )}

      {/* SMS PACKS */}
      {subTab === 'sms-packs' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
            <button style={styles.primaryBtn} onClick={openSmsPackCreate}>+ Novo Pack SMS</button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '1rem' }}>
            {smsPacks.map(p => (
              <div key={p.id} style={{ ...styles.tableCard, textAlign: 'center', opacity: p.isActive ? 1 : 0.5, position: 'relative' }}>
                <div style={{ position: 'absolute', top: '8px', right: '8px', display: 'flex', gap: '0.25rem' }}>
                  <button style={{ ...styles.tinyBtn, background: 'var(--accent-soft)', color: 'var(--accent-primary)' }} onClick={() => openSmsPackEdit(p)}>✏️</button>
                  <button style={{ ...styles.tinyBtn, background: 'var(--error-bg)', color: 'var(--error)' }} onClick={() => handleSmsPackDelete(p)}>🗑️</button>
                </div>
                <div style={{ fontSize: '2.5rem' }}>{p.icon || '📱'}</div>
                <h3 style={{ margin: '0.5rem 0 0.25rem' }}>{p.name}</h3>
                {p.description && <p style={{ color: 'var(--text-muted)', fontSize: '0.8rem', margin: '0 0 0.5rem' }}>{p.description}</p>}
                <div style={{ fontSize: '2rem', fontWeight: '800', color: 'var(--success)' }}>{p.smsAmount}</div>
                <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>SMS</div>
                <div style={{ fontSize: '1.25rem', fontWeight: '700', color: 'var(--accent-primary)', marginTop: '0.5rem' }}>€{p.price?.toFixed(2)}</div>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>€{(p.price / p.smsAmount)?.toFixed(3)} / SMS</div>
                {!p.isActive && <div style={{ marginTop: '0.5rem', padding: '2px 8px', background: 'var(--error-bg)', color: 'var(--error)', borderRadius: '8px', fontSize: '0.75rem', display: 'inline-block' }}>Desactivado</div>}
              </div>
            ))}
            {smsPacks.length === 0 && <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '3rem' }}>Nenhum pack de SMS criado. Clique em "+ Novo Pack SMS".</div>}
          </div>
        </div>
      )}

      {/* PURCHASE HISTORY */}
      {subTab === 'history' && (
        <div style={styles.tableCard}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Data</th>
                <th style={styles.th}>Instância</th>
                <th style={styles.th}>Tipo</th>
                <th style={styles.th}>Pack</th>
                <th style={styles.th}>Quantidade</th>
                <th style={styles.th}>Preço</th>
                <th style={styles.th}>Notas</th>
              </tr>
            </thead>
            <tbody>
              {purchases.length === 0 ? (
                <tr><td colSpan={7} style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>Sem compras registadas</td></tr>
              ) : purchases.map(p => (
                <tr key={p.id}>
                  <td style={styles.td}>{new Date(p.purchasedAt).toLocaleDateString('pt-PT')}</td>
                  <td style={styles.td}><strong>{p.gymName}</strong></td>
                  <td style={styles.td}>
                    <span style={{ padding: '2px 8px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: '600',
                      background: p.creditType === 'tokens' ? 'var(--accent-soft)' : 'var(--success-bg)',
                      color: p.creditType === 'tokens' ? 'var(--accent-primary)' : 'var(--success)'
                    }}>
                      {p.creditType === 'tokens' ? '🤖 Tokens' : '📱 SMS'}
                    </span>
                  </td>
                  <td style={styles.td}>{p.packName}</td>
                  <td style={{ ...styles.td, fontWeight: '700' }}>{p.amount}</td>
                  <td style={{ ...styles.td, color: 'var(--warning)' }}>€{p.price?.toFixed(2)}</td>
                  <td style={styles.td}>{p.notes || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* TOKEN PACK FORM MODAL */}
      {showTokenPackForm && (
        <Modal title={editTokenPackId ? '✏️ Editar Pack Tokens' : '🤖 Novo Pack de Tokens IA'} onClose={() => { setShowTokenPackForm(false); setEditTokenPackId(null); }}>
          <form onSubmit={handleTokenPackSubmit}>
            <div style={styles.formGrid}>
              <FormField label="Nome *" value={tokenPackForm.name} onChange={v => setTokenPackForm({...tokenPackForm, name: v})} required />
              <FormField label="Ícone" value={tokenPackForm.icon} onChange={v => setTokenPackForm({...tokenPackForm, icon: v})} />
            </div>
            <FormField label="Descrição" value={tokenPackForm.description} onChange={v => setTokenPackForm({...tokenPackForm, description: v})} textarea />
            <div style={styles.formGrid}>
              <FormField label="Quantidade Tokens *" type="number" value={tokenPackForm.tokenAmount} onChange={v => setTokenPackForm({...tokenPackForm, tokenAmount: v})} required />
              <FormField label="Preço (€) *" type="number" value={tokenPackForm.price} onChange={v => setTokenPackForm({...tokenPackForm, price: v})} required />
            </div>
            {tokenPackForm.tokenAmount && tokenPackForm.price && (
              <div style={{ background: 'var(--success-bg)', padding: '0.75rem', borderRadius: '8px', margin: '0.5rem 0', color: 'var(--success)', fontSize: '0.85rem' }}>
                💡 Custo por 100 tokens: <strong>€{(parseFloat(tokenPackForm.price) / parseInt(tokenPackForm.tokenAmount) * 100).toFixed(2)}</strong>
              </div>
            )}
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
              <button type="button" style={styles.secondaryBtn} onClick={() => { setShowTokenPackForm(false); setEditTokenPackId(null); }}>Cancelar</button>
              <button type="submit" style={styles.primaryBtn}>{editTokenPackId ? 'Guardar' : 'Criar Pack'}</button>
            </div>
          </form>
        </Modal>
      )}

      {/* SMS PACK FORM MODAL */}
      {showSmsPackForm && (
        <Modal title={editSmsPackId ? '✏️ Editar Pack SMS' : '📱 Novo Pack de SMS'} onClose={() => { setShowSmsPackForm(false); setEditSmsPackId(null); }}>
          <form onSubmit={handleSmsPackSubmit}>
            <div style={styles.formGrid}>
              <FormField label="Nome *" value={smsPackForm.name} onChange={v => setSmsPackForm({...smsPackForm, name: v})} required />
              <FormField label="Ícone" value={smsPackForm.icon} onChange={v => setSmsPackForm({...smsPackForm, icon: v})} />
            </div>
            <FormField label="Descrição" value={smsPackForm.description} onChange={v => setSmsPackForm({...smsPackForm, description: v})} textarea />
            <div style={styles.formGrid}>
              <FormField label="Quantidade SMS *" type="number" value={smsPackForm.smsAmount} onChange={v => setSmsPackForm({...smsPackForm, smsAmount: v})} required />
              <FormField label="Preço (€) *" type="number" value={smsPackForm.price} onChange={v => setSmsPackForm({...smsPackForm, price: v})} required />
            </div>
            {smsPackForm.smsAmount && smsPackForm.price && (
              <div style={{ background: 'var(--success-bg)', padding: '0.75rem', borderRadius: '8px', margin: '0.5rem 0', color: 'var(--success)', fontSize: '0.85rem' }}>
                💡 Custo por SMS: <strong>€{(parseFloat(smsPackForm.price) / parseInt(smsPackForm.smsAmount)).toFixed(3)}</strong>
              </div>
            )}
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
              <button type="button" style={styles.secondaryBtn} onClick={() => { setShowSmsPackForm(false); setEditSmsPackId(null); }}>Cancelar</button>
              <button type="submit" style={styles.primaryBtn}>{editSmsPackId ? 'Guardar' : 'Criar Pack'}</button>
            </div>
          </form>
        </Modal>
      )}

      {/* PURCHASE (ATRIBUIR CRÉDITOS) MODAL */}
      {showPurchaseForm && (
        <Modal title="🪙 Atribuir Créditos a Instância" onClose={() => setShowPurchaseForm(false)}>
          <form onSubmit={handlePurchase}>
            <div style={{ marginBottom: '1rem' }}>
              <label style={styles.label}>Instância</label>
              <select style={styles.input} value={purchaseForm.gymInstanceId} onChange={e => setPurchaseForm({...purchaseForm, gymInstanceId: e.target.value})} required>
                <option value="">Selecione...</option>
                {instanceList.map(i => <option key={i.id} value={i.id}>{i.name}</option>)}
              </select>
            </div>
            <div style={{ marginBottom: '1rem' }}>
              <label style={styles.label}>Tipo de Crédito</label>
              <select style={styles.input} value={purchaseForm.creditType} onChange={e => setPurchaseForm({...purchaseForm, creditType: e.target.value, packId: ''})}>
                <option value="tokens">🤖 Tokens IA</option>
                <option value="sms">📱 SMS</option>
              </select>
            </div>
            <div style={{ marginBottom: '1rem' }}>
              <label style={styles.label}>Pack</label>
              <select style={styles.input} value={purchaseForm.packId} onChange={e => setPurchaseForm({...purchaseForm, packId: e.target.value})} required>
                <option value="">Selecione um pack...</option>
                {activePacks.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.icon} {p.name} — {purchaseForm.creditType === 'tokens' ? `${p.tokenAmount} tokens` : `${p.smsAmount} SMS`} (€{p.price?.toFixed(2)})
                  </option>
                ))}
              </select>
            </div>
            {purchaseForm.packId && (
              <div style={{ background: 'var(--info-bg)', padding: '0.75rem', borderRadius: '8px', marginBottom: '1rem', color: 'var(--info-text)', fontSize: '0.85rem' }}>
                {(() => {
                  const pack = activePacks.find(p => p.id === parseInt(purchaseForm.packId));
                  if (!pack) return null;
                  return <>
                    ✅ <strong>{pack.name}</strong>: {purchaseForm.creditType === 'tokens' ? `${pack.tokenAmount} tokens` : `${pack.smsAmount} SMS`} — <strong>€{pack.price?.toFixed(2)}</strong>
                    <br/>
                    <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>ℹ️ O Admin verá apenas o saldo de créditos, não o valor em euros.</span>
                  </>;
                })()}
              </div>
            )}
            <FormField label="Notas (opcional)" value={purchaseForm.notes} onChange={v => setPurchaseForm({...purchaseForm, notes: v})} textarea />
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
              <button type="button" style={styles.secondaryBtn} onClick={() => setShowPurchaseForm(false)}>Cancelar</button>
              <button type="submit" style={styles.primaryBtn}>Atribuir Créditos</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

// ============================================
// SSH TAB (migrated from old dashboard)
// ============================================
function SshTab() {
  const [sshList, setSshList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: '', host: '', port: 22, username: '', password: '', privateKeyPath: '', isDefault: false });

  const loadData = useCallback(async () => {
    try {
      const res = await sshConfigs.getAll();
      setSshList(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await sshConfigs.create(form);
      toast.success('Configuração SSH criada!');
      setShowCreate(false);
      loadData();
    } catch (e) { toast.error(apiError(e, 'Erro ao criar configuração SSH')); }
  };

  const handleTest = async (id) => {
    try {
      await sshConfigs.testConnection(id);
      toast.success('Conexão SSH OK!');
      loadData();
    } catch (e) { toast.error('Falha na conexão SSH'); }
  };

  if (loading) return <Spinner />;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1 style={styles.pageTitle}>🔧 Configurações SSH</h1>
        <button style={styles.primaryBtn} onClick={() => setShowCreate(true)}>+ Nova Config SSH</button>
      </div>

      <div style={styles.tableCard}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Nome</th>
              <th style={styles.th}>Host</th>
              <th style={styles.th}>Porta</th>
              <th style={styles.th}>Utilizador</th>
              <th style={styles.th}>Autenticação</th>
              <th style={styles.th}>Default</th>
              <th style={styles.th}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {sshList.map(s => (
              <tr key={s.id}>
                <td style={styles.td}><strong>{s.name}</strong></td>
                <td style={styles.td}>{s.host}</td>
                <td style={styles.td}>{s.port}</td>
                <td style={styles.td}>{s.username}</td>
                <td style={styles.td}>
                  <span className={`su-badge ${s.authMethod === 'chave' ? 'su-badge-success' : s.authMethod === 'nenhuma' ? 'su-badge-error' : 'su-badge-warning'}`}>
                    {s.authMethod || '—'}
                  </span>
                </td>
                <td style={styles.td}>{s.isDefault ? '✅' : ''}</td>
                <td style={styles.td}>
                  <button style={{ ...styles.tinyBtn, background: 'var(--accent-soft)', color: 'var(--accent-primary)' }} onClick={() => handleTest(s.id)}>🔌 Testar</button>
                  <button style={{ ...styles.tinyBtn, background: 'var(--error-bg)', color: 'var(--error)', marginLeft: '0.25rem' }} onClick={() => { if (window.confirm('Eliminar?')) sshConfigs.delete(s.id).then(() => { toast.success('Eliminado'); loadData(); }); }}>🗑️</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showCreate && (
        <Modal title="🔧 Nova Configuração SSH" onClose={() => setShowCreate(false)}>
          <form onSubmit={handleCreate}>
            <div style={styles.formGrid}>
              <FormField label="Nome *" value={form.name} onChange={v => setForm({...form, name: v})} required />
              <FormField label="Host *" value={form.host} onChange={v => setForm({...form, host: v})} required />
              <FormField label="Porta" type="number" value={form.port} onChange={v => setForm({...form, port: parseInt(v)})} />
              <FormField label="Utilizador *" value={form.username} onChange={v => setForm({...form, username: v})} required />
              <FormField label="Chave privada (caminho no servidor)" value={form.privateKeyPath} onChange={v => setForm({...form, privateKeyPath: v})} />
              <div style={{ gridColumn: '1 / -1', marginTop: '-0.5rem', marginBottom: '0.75rem', fontSize: '0.75rem', color: 'var(--text-faint)' }}>
                Recomendado. Ex.: <span style={styles.monoInline}>/run/secrets/ssh_deploy_key</span> — ficheiro montado no container. Guarda-se só o caminho, nunca a chave.
              </div>
              <FormField label="Password (legado — só sem chave)" type="password" value={form.password} onChange={v => setForm({...form, password: v})} />
            </div>
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
              <button type="button" style={styles.secondaryBtn} onClick={() => setShowCreate(false)}>Cancelar</button>
              <button type="submit" style={styles.primaryBtn}>Criar</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

// ============================================
// NOTIFICATIONS TAB (migrated from old dashboard)
// ============================================
function NotificationsTab() {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ title: '', content: '', type: 'info', isActive: true });

  const loadData = useCallback(async () => {
    try {
      const token = localStorage.getItem('superuser_token');
      const res = await fetch('/api/superuser/notifications', { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) setAnnouncements(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('superuser_token');
      const url = editId ? `/api/superuser/notifications/${editId}` : '/api/superuser/notifications';
      const res = await fetch(url, {
        method: editId ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(form)
      });
      if (res.ok) {
        toast.success(editId ? 'Atualizado!' : 'Criado!');
        setShowForm(false); setEditId(null);
        setForm({ title: '', content: '', type: 'info', isActive: true });
        loadData();
      }
    } catch (e) { toast.error('Erro'); }
  };

  const handleDelete = async (id) => {
    try {
      const token = localStorage.getItem('superuser_token');
      await fetch(`/api/superuser/notifications/${id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
      toast.success('Desactivado');
      loadData();
    } catch (e) { toast.error('Erro'); }
  };

  if (loading) return <Spinner />;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1 style={styles.pageTitle}>🔔 Notificações Globais</h1>
        <button style={styles.primaryBtn} onClick={() => { setEditId(null); setForm({ title: '', content: '', type: 'info', isActive: true }); setShowForm(true); }}>+ Novo Anúncio</button>
      </div>

      <div style={styles.tableCard}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Título</th>
              <th style={styles.th}>Tipo</th>
              <th style={styles.th}>Estado</th>
              <th style={styles.th}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {announcements.length === 0 ? (
              <tr><td colSpan={4} style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>Sem anúncios</td></tr>
            ) : announcements.map(a => (
              <tr key={a.id}>
                <td style={styles.td}>
                  <strong>{a.title}</strong>
                  <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{a.content?.substring(0, 60)}{a.content?.length > 60 ? '...' : ''}</div>
                </td>
                <td style={styles.td}>{getTypeEmoji(a.type)} {a.type}</td>
                <td style={styles.td}>
                  <span style={{ color: a.isActive ? 'var(--success)' : 'var(--error)', fontWeight: '600' }}>
                    {a.isActive ? '✅ Activo' : '❌ Inactivo'}
                  </span>
                </td>
                <td style={styles.td}>
                  <button style={{ ...styles.tinyBtn, background: 'var(--accent-soft)', color: 'var(--accent-primary)' }} onClick={() => { setEditId(a.id); setForm({ title: a.title, content: a.content, type: a.type || 'info', isActive: a.isActive }); setShowForm(true); }}>✏️</button>
                  <button style={{ ...styles.tinyBtn, background: 'var(--error-bg)', color: 'var(--error)', marginLeft: '0.25rem' }} onClick={() => handleDelete(a.id)}>🗑️</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showForm && (
        <Modal title={editId ? '✏️ Editar Anúncio' : '🔔 Novo Anúncio Global'} onClose={() => setShowForm(false)}>
          <form onSubmit={handleSubmit}>
            <FormField label="Título *" value={form.title} onChange={v => setForm({...form, title: v})} required />
            <FormField label="Mensagem *" value={form.content} onChange={v => setForm({...form, content: v})} textarea required />
            <div style={styles.formGrid}>
              <div>
                <label style={styles.label}>Tipo</label>
                <select style={styles.input} value={form.type} onChange={e => setForm({...form, type: e.target.value})}>
                  <option value="info">ℹ️ Informação</option>
                  <option value="warning">⚠️ Aviso</option>
                  <option value="success">✅ Sucesso</option>
                  <option value="promo">🎁 Promoção</option>
                </select>
              </div>
              <div>
                <label style={styles.label}>Estado</label>
                <select style={styles.input} value={form.isActive ? 'active' : 'inactive'} onChange={e => setForm({...form, isActive: e.target.value === 'active'})}>
                  <option value="active">✅ Activo</option>
                  <option value="inactive">❌ Inactivo</option>
                </select>
              </div>
            </div>
            <div style={{ background: 'var(--warning-bg)', padding: '0.75rem', borderRadius: '8px', margin: '1rem 0', color: 'var(--warning-text)', fontSize: '0.85rem' }}>
              ⚠️ Este anúncio será visível para TODOS os utilizadores de TODAS as instâncias.
            </div>
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button type="button" style={styles.secondaryBtn} onClick={() => setShowForm(false)}>Cancelar</button>
              <button type="submit" style={styles.primaryBtn}>{editId ? 'Atualizar' : 'Criar'}</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

// ============================================
// SHARED COMPONENTS
// ============================================

function Spinner() {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.625rem', height: '40vh', color: 'var(--text-muted)', fontSize: '0.875rem' }}>
      <Loader2 size={18} style={{ animation: 'su-spin 1s linear infinite' }} />
      A carregar...
    </div>
  );
}

function KpiCard({ icon, label, value, color }) {
  return (
    <div className="su-card" style={{ padding: '1rem 1.125rem', display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <span style={{ width: '6px', height: '6px', borderRadius: '9999px', background: color, flexShrink: 0 }} />
        <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: '500' }}>{label}</span>
      </div>
      <div style={{ fontSize: '1.375rem', fontWeight: '700', color: 'var(--text-primary)', letterSpacing: '-0.01em', wordBreak: 'break-word' }}>{value}</div>
    </div>
  );
}

function Modal({ title, children, onClose }) {
  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h2 style={{ margin: 0, fontSize: '1.25rem' }}>{title}</h2>
          <button style={{ background: 'none', border: 'none', fontSize: '1.25rem', cursor: 'pointer' }} onClick={onClose}>✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function FormField({ label, value, onChange, type = 'text', textarea, required, placeholder }) {
  return (
    <div style={{ marginBottom: '1rem' }}>
      <label style={styles.label}>{label}</label>
      {textarea ? (
        <textarea style={{ ...styles.input, minHeight: '80px', resize: 'vertical' }} value={value} onChange={e => onChange(e.target.value)} required={required} placeholder={placeholder} />
      ) : (
        <input type={type} style={styles.input} value={value} onChange={e => onChange(e.target.value)} required={required} placeholder={placeholder} step={type === 'number' ? 'any' : undefined} />
      )}
    </div>
  );
}

/// Fases de um deploy, na ordem em que correram. Existe porque um "Erro" sozinho obrigava
/// a ir ao servidor ler logs para saber se tinha sido a porta, a imagem ou a base de dados.
function DeployReport({ report, onClose }) {
  const steps = Array.isArray(report.steps) ? report.steps : [];
  return (
    <div style={{
      background: 'var(--bg-secondary)', border: `1px solid ${report.success ? 'var(--success-text)' : 'var(--error-text)'}`,
      borderRadius: '8px', padding: '1rem', marginBottom: '1.5rem',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem', marginBottom: '0.75rem' }}>
        <div>
          <strong style={{ color: report.success ? 'var(--success-text)' : 'var(--error-text)' }}>
            {report.success ? 'Deploy concluído' : 'Deploy falhou'} — {report.name}
          </strong>
          <div style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', marginTop: '2px' }}>{report.message}</div>
        </div>
        <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '1.1rem', lineHeight: 1 }}>×</button>
      </div>
      <ol style={{ margin: 0, paddingLeft: '1.1rem', fontSize: '0.8rem', lineHeight: '1.9' }}>
        {steps.map((s, i) => (
          <li key={i} style={{ color: s.ok ? 'var(--text-secondary)' : 'var(--error-text)' }}>
            <strong>{s.phase}</strong>{s.detail ? ` — ${s.detail}` : ''}
          </li>
        ))}
      </ol>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    0: { bg: 'var(--warning-bg)', color: 'var(--warning-text)', label: 'Pendente' },
    1: { bg: 'var(--accent-soft)', color: 'var(--accent-primary)', label: 'Deploying' },
    2: { bg: 'var(--success-bg)', color: 'var(--success-text)', label: 'Running' },
    3: { bg: 'var(--bg-tertiary)', color: 'var(--text-muted)', label: 'Stopped' },
    4: { bg: 'var(--error-bg)', color: 'var(--error-text)', label: 'Error' },
    5: { bg: 'var(--warning-bg)', color: 'var(--warning-text)', label: 'Sem container' },
    6: { bg: 'var(--accent-soft)', color: 'var(--accent-primary)', label: 'A instalar' },
    7: { bg: 'var(--accent-soft)', color: 'var(--accent-primary)', label: 'A atualizar' },
    8: { bg: 'var(--error-bg)', color: 'var(--error-text)', label: 'Sem resposta' },
  };
  const s = map[status] || map[0];
  return <span style={{ padding: '2px 10px', borderRadius: '4px', fontSize: '0.72rem', fontWeight: '600', backgroundColor: s.bg, color: s.color, letterSpacing: '0.02em' }}>{s.label}</span>;
}

function SubStatusBadge({ status }) {
  const map = {
    active: { bg: 'var(--success-bg)', color: 'var(--success)', label: '✅ Activa' },
    suspended: { bg: 'var(--warning-bg)', color: 'var(--warning)', label: '⏸ Suspensa' },
    cancelled: { bg: 'var(--error-bg)', color: 'var(--error)', label: '❌ Cancelada' },
    trial: { bg: 'var(--accent-soft)', color: 'var(--accent-primary)', label: '🎁 Trial' },
  };
  const s = map[status] || { bg: 'var(--bg-tertiary)', color: 'var(--text-muted)', label: status };
  return <span style={{ padding: '2px 8px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: '600', backgroundColor: s.bg, color: s.color }}>{s.label}</span>;
}

function InvoiceStatusBadge({ status }) {
  const map = {
    pending: { bg: 'var(--warning-bg)', color: 'var(--warning)', label: '⏳ Pendente' },
    paid: { bg: 'var(--success-bg)', color: 'var(--success)', label: '✅ Paga' },
    overdue: { bg: 'var(--error-bg)', color: 'var(--error)', label: '🔴 Em Atraso' },
    cancelled: { bg: 'var(--bg-tertiary)', color: 'var(--text-muted)', label: '❌ Anulada' },
  };
  const s = map[status] || { bg: 'var(--bg-tertiary)', color: 'var(--text-muted)', label: status };
  return <span style={{ padding: '2px 8px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: '600', backgroundColor: s.bg, color: s.color }}>{s.label}</span>;
}

// ============================================
// HELPERS
// ============================================
function getAuditIcon(action) {
  const map = { blocked: '🚫', unblocked: '✅', plan_assigned: '📋', plan_changed: '📋', invoice_created: '📄', invoice_status_changed: '📄', payment_received: '💰', subscription_suspended: '⏸', subscription_reactivated: '▶' };
  return map[action] || '📝';
}

function getPaymentMethodLabel(method) {
  const map = { transfer: '🏦 Transferência', mbway: '📱 MBWay', card: '💳 Cartão', cash: '💵 Numerário' };
  return map[method] || method;
}

function getTypeEmoji(type) {
  return { info: 'ℹ️', warning: '⚠️', success: '✅', promo: '🎁' }[type] || 'ℹ️';
}

// Delineado em vez de bloco sólido: numa linha com 6 acções, seis rectângulos
// saturados competem entre si e nenhum se lê. A cor fica na borda e no texto.
function instActionBtn(color) {
  return {
    padding: '0.3rem 0.7rem', fontSize: '0.75rem', fontWeight: '600',
    border: `1px solid ${color}`, borderRadius: 'var(--radius-sm)', cursor: 'pointer',
    background: 'transparent', color, whiteSpace: 'nowrap',
    fontFamily: 'var(--font-sans)', transition: 'background-color 150ms ease',
  };
}

// ============================================
// SMS GATEWAY TAB
// ============================================
function AndroidBuildsTab() {
  const [builds, setBuilds] = useState([]);
  const [instances, setInstances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newBuild, setNewBuild] = useState({ gymInstanceId: '', buildType: 'Genérico', version: '1.0.0' });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [buildsRes, instancesRes] = await Promise.all([
        androidBuilds.getAll(),
        instances.getAll()
      ]);
      setBuilds(Array.isArray(buildsRes.data) ? buildsRes.data : []);
      setInstances(Array.isArray(instancesRes.data) ? instancesRes.data : []);
    } catch (e) {
      toast.error('Erro ao carregar builds');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await androidBuilds.create({
        gymInstanceId: parseInt(newBuild.gymInstanceId),
        buildType: newBuild.buildType,
        version: newBuild.version
      });
      toast.success('Build criado! O processamento foi iniciado.');
      setShowCreate(false);
      setNewBuild({ gymInstanceId: '', buildType: 'Genérico', version: '1.0.0' });
      loadData();
    } catch (e) {
      toast.error('Erro ao criar build');
    }
  };

  const handleDownload = async (build) => {
    if (build.status !== 'Success') {
      toast.error('APK não disponível para download');
      return;
    }
    try {
      const response = await androidBuilds.download(build.id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${build.gymInstanceName}-${build.buildType}-v${build.version}.apk`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Download iniciado');
    } catch (e) {
      toast.error('Erro ao fazer download');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminar este build? O ficheiro APK será removido do servidor.')) return;
    try {
      await androidBuilds.delete(id);
      toast.success('Build eliminado');
      loadData();
    } catch (e) {
      toast.error('Erro ao eliminar');
    }
  };

  const formatBytes = (bytes) => {
    if (!bytes) return '-';
    const mb = bytes / (1024 * 1024);
    return mb.toFixed(2) + ' MB';
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '-';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  const getStatusBadge = (status) => {
    const styles = {
      Success: { background: 'var(--success-bg)', color: 'var(--success-text)', padding: '4px 12px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: '600' },
      Failed: { background: 'var(--error-bg)', color: 'var(--error-text)', padding: '4px 12px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: '600' },
      Building: { background: 'var(--warning-bg)', color: 'var(--warning-text)', padding: '4px 12px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: '600' },
      Pending: { background: 'var(--accent-soft)', color: 'var(--accent-primary)', padding: '4px 12px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: '600' }
    };
    return <span style={styles[status] || styles.Pending}>{status === 'Success' ? 'Sucesso' : status === 'Failed' ? 'Falhou' : status === 'Building' ? 'A Compilar' : 'Pendente'}</span>;
  };

  if (loading) return <Spinner />;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1 style={styles.pageTitle}>📱 Histórico de Builds Android</h1>
        <button style={styles.primaryBtn} onClick={() => setShowCreate(true)}>+ Novo Build</button>
      </div>

      <div style={styles.tableCard}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>DATA</th>
              <th style={styles.th}>TIPO</th>
              <th style={styles.th}>UTILIZADOR</th>
              <th style={styles.th}>STATUS</th>
              <th style={styles.th}>TAMANHO</th>
              <th style={styles.th}>AÇÕES</th>
            </tr>
          </thead>
          <tbody>
            {builds.map(build => (
              <tr key={build.id}>
                <td style={styles.td}>{new Date(build.createdAt).toLocaleString('pt-PT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</td>
                <td style={styles.td}>{build.buildType}</td>
                <td style={styles.td}>{build.requestedBy || '-'}</td>
                <td style={styles.td}>{getStatusBadge(build.status)}</td>
                <td style={styles.td}>{formatBytes(build.apkSize)}</td>
                <td style={styles.td}>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button 
                      style={{ ...styles.tinyBtn, background: 'var(--accent-primary)', color: 'white' }}
                      onClick={() => handleDownload(build)}
                      disabled={build.status !== 'Success'}
                    >
                      ⬇️ Download
                    </button>
                    <button 
                      style={{ ...styles.tinyBtn, background: 'var(--error)', color: 'white' }}
                      onClick={() => handleDelete(build.id)}
                    >
                      🗑️ Eliminar
                    </button>
                  </div>
                  {build.status === 'Failed' && build.errorMessage && (
                    <div style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: 'var(--error)', background: 'var(--error-bg)', padding: '8px', borderRadius: '6px', whiteSpace: 'pre-wrap' }}>
                      {build.errorMessage}
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {builds.length === 0 && (
              <tr>
                <td colSpan="6" style={{ ...styles.td, textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>
                  Nenhum build encontrado. Clique em "+ Novo Build" para começar.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showCreate && (
        <Modal title="🏗️ Novo Build Android" onClose={() => setShowCreate(false)}>
          <form onSubmit={handleCreate}>
            <div style={styles.formGrid}>
              <FormField 
                label="Instância *" 
                type="select"
                value={newBuild.gymInstanceId} 
                onChange={v => setNewBuild({...newBuild, gymInstanceId: v})}
                options={[{value: '', label: 'Selecione...'}, ...instances.map(i => ({value: i.id, label: i.name}))]}
                required 
              />
              <FormField 
                label="Tipo de Build" 
                type="select"
                value={newBuild.buildType} 
                onChange={v => setNewBuild({...newBuild, buildType: v})}
                options={[
                  {value: 'Genérico', label: 'Genérico'},
                  {value: 'Debug', label: 'Debug'},
                  {value: 'Release', label: 'Release'}
                ]}
              />
              <FormField 
                label="Versão" 
                value={newBuild.version} 
                onChange={v => setNewBuild({...newBuild, version: v})}
                placeholder="1.0.0"
              />
            </div>
            <div style={{ marginTop: '1.5rem', fontSize: '0.875rem', color: 'var(--text-muted)', background: 'var(--bg-tertiary)', padding: '1rem', borderRadius: '8px' }}>
              <strong>ℹ️ Nota:</strong> O build será processado em background. Pode demorar alguns minutos. Atualize a página para ver o progresso.
            </div>
            <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.5rem' }}>
              <button type="submit" style={styles.primaryBtn}>Criar Build</button>
              <button type="button" style={styles.secondaryBtn} onClick={() => setShowCreate(false)}>Cancelar</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

// ============================================
// STYLES
// ============================================
const styles = {
  root: { display: 'flex', minHeight: '100vh', background: 'var(--bg-primary)' },
  sidebar: { width: '240px', background: 'var(--bg-secondary)', borderRight: '1px solid var(--border-color)', display: 'flex', flexDirection: 'column', position: 'sticky', top: 0, height: '100vh', flexShrink: 0 },
  sidebarHeader: { padding: '1.5rem 1.25rem', display: 'flex', alignItems: 'center', gap: '0.75rem', borderBottom: '1px solid var(--border-color)' },
  sidebarNav: { flex: 1, padding: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.125rem', overflowY: 'auto' },
  sidebarFooter: { padding: '0.75rem', borderTop: '1px solid var(--border-color)' },
  mainContent: { flex: 1, padding: '1.75rem 2rem', overflow: 'auto', maxWidth: '1400px' },
  pageTitle: { margin: '0 0 1.5rem', fontSize: '1.5rem', fontWeight: '700', color: 'var(--text-primary)', letterSpacing: '-0.01em' },
  kpiGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.75rem' },
  tableCard: { backgroundColor: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-lg)', padding: '1.25rem', overflowX: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' },
  th: { textAlign: 'left', padding: '0.75rem', borderBottom: '1px solid var(--border-color)', color: 'var(--text-faint)', fontSize: '0.75rem', fontWeight: '500' },
  td: { padding: '0.875rem 0.75rem', borderBottom: '1px solid var(--border-color)', color: 'var(--text-secondary)' },
  primaryBtn: { padding: '0.625rem 1rem', backgroundColor: 'var(--accent-primary)', color: '#fff', border: '1px solid transparent', borderRadius: 'var(--radius-md)', cursor: 'pointer', fontWeight: '600', fontSize: '0.875rem', fontFamily: 'var(--font-sans)' },
  secondaryBtn: { padding: '0.625rem 1rem', backgroundColor: 'transparent', color: 'var(--text-muted)', border: '1px solid var(--border-strong)', borderRadius: 'var(--radius-md)', cursor: 'pointer', fontWeight: '500', fontFamily: 'var(--font-sans)' },
  smallBtn: { padding: '0.375rem 0.75rem', backgroundColor: 'transparent', color: 'var(--text-muted)', border: '1px solid var(--border-strong)', borderRadius: 'var(--radius-sm)', cursor: 'pointer', fontWeight: '500', fontSize: '0.8rem', fontFamily: 'var(--font-sans)' },
  tinyBtn: { padding: '0.25rem 0.625rem', border: '1px solid transparent', borderRadius: 'var(--radius-sm)', cursor: 'pointer', fontWeight: '600', fontSize: '0.75rem', fontFamily: 'var(--font-sans)' },
  backBtn: { padding: '0.5rem 0.875rem', backgroundColor: 'transparent', color: 'var(--text-muted)', border: '1px solid var(--border-strong)', borderRadius: 'var(--radius-md)', cursor: 'pointer', fontWeight: '500', marginBottom: '1rem', fontFamily: 'var(--font-sans)' },
  subTabs: { display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', flexWrap: 'wrap' },
  subTab: { padding: '0.5rem 0.875rem', border: '1px solid var(--border-strong)', background: 'transparent', borderRadius: 'var(--radius-md)', cursor: 'pointer', fontSize: '0.875rem', color: 'var(--text-muted)', fontFamily: 'var(--font-sans)' },
  subTabActive: { backgroundColor: 'var(--accent-primary)', color: '#fff', borderColor: 'var(--accent-primary)' },
  formGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' },
  label: { display: 'block', fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '0.375rem', fontWeight: '500' },
  input: { width: '100%', padding: '0.625rem 0.875rem', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-strong)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: '0.875rem', boxSizing: 'border-box', fontFamily: 'var(--font-sans)' },
  modalOverlay: { position: 'fixed', inset: 0, backgroundColor: 'rgba(2,6,23,0.75)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000, padding: '1rem' },
  modalContent: { backgroundColor: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-lg)', padding: '1.75rem', width: '100%', maxWidth: '600px', maxHeight: '85vh', overflowY: 'auto', boxShadow: 'var(--shadow-xl)', color: 'var(--text-primary)' },
  monoInline: { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace', background: 'var(--bg-tertiary)', color: 'var(--text-secondary)', padding: '2px 6px', borderRadius: '6px', border: '1px solid var(--border-strong)', fontSize: '0.85em' },
  monoBox: { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace', background: 'var(--bg-primary)', color: 'var(--text-secondary)', padding: '6px 10px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-strong)', fontSize: '0.85rem', wordBreak: 'break-all' },
  tipBox: { marginTop: '0.75rem', background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-md)', padding: '0.75rem', color: 'var(--text-secondary)', fontSize: '0.9rem' },
  details: { border: '1px solid var(--border-color)', borderRadius: 'var(--radius-md)', padding: '0.75rem', background: 'var(--bg-card)', marginTop: '0.75rem' },
  summary: { cursor: 'pointer', fontWeight: '700', color: 'var(--text-primary)' },
};

export default SuperUserDashboard;
